import type { Dictionary } from './pt-BR';

/* ============================================================================
 *  ZENDA — interface strings, English
 * ----------------------------------------------------------------------------
 *  Typed against the Portuguese dictionary: a missing key is a compile error,
 *  not a string that quietly renders in the wrong language at runtime.
 *
 *  Written for two readers who could not be more different:
 *
 *   · a Meta App Review reviewer, who has never seen this product and needs to
 *     understand what a screen does within seconds of the video starting;
 *   · someone actually using the product in English.
 *
 *  So it is a translation of meaning, not of words. Where Portuguese leans on
 *  Brazilian context — "CRM", "consultório", the 24-hour window — the English
 *  says what the thing IS.
 * ========================================================================= */

type Vars = Readonly<Record<string, string | number>>;

export const en: Dictionary = {
  nav: {
    team: 'Team',
    dashboard: 'Overview',
    inbox: 'Conversations',
    clients: 'Clients',
    agenda: 'Schedule',
    verification: 'Verification',
    settings: 'Settings',
    sections: 'Sections',
    signOut: 'Sign out',
    language: 'Language',
    collapseMenu: 'Collapse menu',
    expandMenu: 'Expand menu',
  },

  custo: {
    doMes: (mes: string) => `Messages in ${mes}`,
    quantas: (n: number) => (n === 1 ? '1 message' : `${n.toLocaleString('en-US')} messages`),
    estimativa: 'estimate',
    esconder: 'Hide costs',
    debitado: (valor: string, dia: string) => `${valor} billed by Gupshup through ${dia}`,
    semExtrato: 'Gupshup has not closed any day of this month yet',
    mostrar: 'Show costs',
    explicacao:
      'Estimate of what Meta and Gupshup charge for your messages this month. For your information only: none of it is billed to you.',
    partes: (meta: string, parceiro: string) => `Meta ${meta} + Gupshup ${parceiro}`,
    daMensagem: (partes: string) => `Estimated cost: ${partes}`,
    gratisNaMeta: (parceiro: string) => `Free on Meta, Gupshup ${parceiro}`,
    categoria: {
      service: 'reply',
      utility: 'utility',
      marketing: 'marketing',
      authentication: 'authentication',
    },
  },

  pages: {
    team: { title: 'Team', subtitle: 'Who works with you, and which doctors each one shares' },
    dashboard: { title: 'Overview', subtitle: 'What needs your attention right now' },
    inbox: {
      title: 'Conversations',
      subtitle: 'Every patient, from every client, in a single list',
    },
    clients: {
      title: 'Clients',
      subtitle: 'The doctors you work for: billing, connection and volume',
    },
    agenda: { title: 'Schedule', subtitle: 'Appointments by doctor' },
    settings: { title: 'Settings', subtitle: 'Language, tags and message templates' },
    verification: {
      title: 'Integration verification',
      subtitle: 'The API calls Meta requires to approve the app',
    },
  },

  common: {
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    add: 'Add',
    remove: 'Remove',
    close: 'Close',
    edit: 'Edit',
    open: 'Open',
    search: 'Search',
    none: '—',
    loading: 'Loading…',
    understood: 'Got it',
    copy: 'Copy',
    copied: 'Copied',
    day: 'day',
    days: 'days',
    month: 'month',
    months: 'months',
    minute: 'min',
    hour: 'h',
    perMonth: 'per month',
    dueDate: 'due date',
    now: 'just now',
    networkError: 'Could not reach the server. Check your connection and try again.',
  },

  player: {
    tocar: 'Play',
    pausar: 'Pause',
    fechar: 'Close the player',
    posicao: 'Audio position',
    audio: 'Audio',
    ouvir: 'Listen',
    irParaConversa: 'Go to the conversation',
  },

  config: {
    nome: 'Your name in conversations',
    nomeSub:
      'How the team sees you in messages, and what replaces {atendente}. Example: "Ana" instead of "Ana Paula Souza". Empty uses your full name.',
    nomeSalvo: 'Name saved.',
    language: 'Language',
    languageSub:
      'Applies to you only. It does not change the language of messages sent to patients.',
    tags: 'Tags',
    tagsSub:
      'Labels to find conversations later. Example: "waiting for test results". They apply to every client.',
    tagColor: 'Tag colour',
    tagName: 'Tag name',
    tagPlaceholder: 'waiting on labs, insurance, reschedule…',
    create: 'Create',
    unused: 'unused',
    usedIn: (n: number) => (n === 1 ? '1 conversation' : `${n} conversations`),
    confirmDelete: (n: number) =>
      n === 1 ? 'Remove from 1 conversation?' : `Remove from ${n} conversations?`,
    no: 'No',
    delete: 'Delete',
    deleteTag: 'Delete this tag',
    tagsEmpty: 'No tags yet',
    tagsEmptyText: 'Tags make things findable later: “waiting on labs”, “insurance”, “reschedule”.',

    quick: 'Canned messages',
    quickSub:
      'Replies you send all the time, so you do not retype them. Example: appointment confirmation. They only go out with the 24-hour window open, because they skip Meta approval.',
    title: 'Title',
    titlePlaceholder: 'Appointment confirmation',
    appearsIn: 'Appears in',
    allDoctors: 'Every doctor',
    onlyClient: (nome: string) => `Only ${nome}`,
    message: 'Message',
    quickPlaceholder: 'Good morning {paciente}! This is {atendente}, {medico}’s secretary.',
    varsTitle: 'Click to insert into the text:',
    varAlways: (rotulo: string) => `${rotulo}: always filled in`,
    varMaybe: (rotulo: string) => `${rotulo}: only if the patient record has it`,
    varsNote:
      'The struck-through ones depend on the patient record, like date of birth. If the record lacks it, sending waits until you fill it in by hand.',
    varsUnknown: (lista: string) => `${lista} does not exist. Use the buttons above.`,
    quickCreate: 'Create message',
    quickSave: 'Save changes',
    quickEmpty: 'No canned messages yet',
    quickEmptyText: 'Create the first one above. It shows up in the conversation, one click away.',
    scopeAll: 'All',
    confirm: 'Confirm',

    models: 'Template drafts',
    modelsSub:
      "A base text to reuse across doctors, like the day-before reminder. Here it is only saved: Meta approval is requested in each doctor's Templates tab, because each one has their own account.",
    name: 'Name',
    namePlaceholder: 'reminder_day_before',
    category: 'Category',
    categoryUtility: 'Utility: reminder, confirmation, notice',
    categoryMarketing: 'Marketing: promotion, news',
    modelPlaceholder: 'Hi {{1}}, your appointment with {{2}} is tomorrow at {{3}}. Confirm?',
    modelHint:
      "Meta rules: do not start or end with a variable. Zenda rule: no specialty, procedure or diagnosis, because the text shows on the patient's lock screen.",
    modelVars: 'Meta uses numbered variables:',
    examples: (feitos: number, total: number) =>
      `An example for each variable (${feitos} of ${total})`,
    examplesPlaceholder: 'Marcos\nDr. Helena\n9am',
    examplesHint:
      'One per line, in variable order. Meta reads the examples to approve. Example: Marcos, Dr. Helena, 9am.',
    modelSave: 'Save draft',
    modelsEmpty: 'No drafts yet',
    modelsEmptyText: 'Create the ones you repeat for every new doctor, like a day-before reminder.',
    deleteModel: 'Delete draft',
    utility: 'Utility',
    marketing: 'Marketing',
  },

  aviso: {
    soCasaForaDoTurno: 'Outside my shift, only alert for doctors of my operation',
    titulo: 'New message in Zenda',
    corpo: 'A patient replied. Open to read it.',
    secao: 'New message alerts',
    secaoSub:
      'They apply to this browser only. The notification shows when the tab is in the background: with the screen in view, the list already tells you.',
    som: 'Play a sound',
    notificacao: 'Show a system notification',
    bloqueada:
      'The browser blocked notifications for this site. Allow them in the site permissions to turn this on.',
    semSuporte: 'This browser does not support notifications.',
    testar: 'Test',
  },

  login: {
    title: 'Sign in',
    greeting: 'Good to see you again',
    lede: 'The schedules and WhatsApp accounts of every doctor you work for, on one screen.',
    email: 'Email',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    lostAccess: 'Lost access? Whoever runs the operation resets your password.',
    submit: 'Sign in',
    submitting: 'Signing in…',
    failed: 'Could not sign in.',

    panelTitle: 'Every doctor served as if they were the only one',
    panelSub: 'You open one screen, and the day is already sorted.',
  },

  trocarSenha: {
    title: 'Create your password',
    lede: 'You signed in with a temporary password. Choose your own to continue.',
    atual: 'Temporary password',
    nova: 'New password',
    confirmar: 'Repeat the new password',
    regra: (n: number) => `At least ${n} characters.`,
    naoConfere: 'The two new passwords do not match.',
    salvar: 'Save and continue',
    salvando: 'Saving…',
    sair: 'Sign out',
  },

  erroDaTela: {
    title: 'This screen broke',
    lede: 'The error has been recorded. Reload the page to continue.',
    codigo: (c: string) => `Error code: ${c}`,
    recarregar: 'Reload',
  },

  ambiente: {
    staging: 'Test environment',
    local: 'Local environment',
  },

  dashboard: {
    seeAll: 'See all',
    appointmentCount: (n: number) => (n === 1 ? '1 appointment' : `${n} appointments`),
    conversationsOpen: 'Open conversations',
    awaitingReply: 'Awaiting reply',
    windowsClosing: 'Windows closing',
    appointmentsToday: 'Appointments today',
    activeClients: 'Active clients',
    monthlyRevenue: 'Monthly revenue',
    needAttention: 'Need attention',
    overduePayments: 'Overdue payments',
    channelsHealthy: 'Numbers online',
    uniqueRecipients: 'Recipients in 24 h',
    tier: 'Current limit',
    oneNeedsAction: '1 client needs action now',
    manyNeedAction: (n: number) => `${n} clients need action now`,
    andMore: (n: number) => ` · and ${n} more`,
    indicators: 'Indicators',
    clientsAttentionTitle: 'Clients needing action',
    clientsAttentionHint:
      "Dropped connection, no card on the doctor's Meta account, or slipping quality. While it lasts, the client thinks you stopped replying.",
    windowsExpiringTitle: 'Windows closing',
    windowsExpiringHint: 'Past 24 hours, only an approved template reopens the conversation.',
    conversationsWaiting: 'Conversations waiting',
    activeClientsHint: 'Doctors under contract',
    overdueHint: 'Fee overdue and unpaid. Forgetting to charge is revenue that never comes back.',
    waitingTitle: 'Conversations awaiting a reply',
    waitingSub: 'From every client, ordered by the window that closes first',
    waitingEmpty: 'No conversation waiting',
    waitingEmptyText: 'Every message received has been answered. The team is up to date.',
    waitingEmptyNoClients:
      'Conversations show up here once the first client WhatsApp is connected.',
    firstRunTitle: 'Start with the first doctor you serve',
    firstRunText:
      'Add the client, connect their WhatsApp and patient conversations start arriving here.',
    firstRunCta: 'Add first client',
    todayTitle: "Today's schedule",
    openAgenda: 'Open schedule',
    todayEmpty: 'No appointments today',
    todayEmptyText: "Today's schedule is clear. A good moment to confirm the week.",
    windowClosed: 'window closed',
    windowLeft: (tempo: string) => `${tempo} left`,
    openClients: 'Open clients',
    openInbox: 'Open conversations',
    openTotalHint: (n: number) => `${n} open conversations in total`,
    revenueHint: (n: number) => `${n} active clients in your operation`,
  },

  inbox: {
    fichaAcoes: {
      marcar: 'Book',
      desmarcar: 'Cancel',
      nao: 'No',
      serie: 'series',
      encaixe: 'squeezed in',
      desmarcarSo: 'Cancel only this appointment',
      desmarcarTodas: 'Cancel all',
      desmarcarHora: (hora: string) => `Cancel ${hora}?`,
      desmarcarN: (n: number) => `Cancel the ${n} appointments?`,
    },
    tambemFalaCom: 'Also talks to',
    marcarCom: 'Book with',
    desta: 'this conversation',
    marcadaComOutro: (nome: string) =>
      `Appointment booked with ${nome}. It shows on ${nome}'s schedule; tell the patient through the conversation with that doctor.`,
    comOutrosMedicos: 'With other doctors',
    abrirFicha: 'Record',
    abrirFichaDica: 'Patient details, insurance and dependents',
    incluiDependentes: (n: number) =>
      n === 1 ? 'One of them is for a dependent.' : `${n} of them are for dependents.`,
    sumiu: 'This conversation is not available to you',
    sumiuTexto:
      'The doctor may have left the team, or the link is wrong. Pick another conversation from the list.',
    foco: {
      daCasa: 'From your operation',
      daDona: (nome: string) => `From ${nome}`,
      foraDoAlcance: (n: number) =>
        n === 1
          ? '1 doctor in your focus is no longer shared.'
          : `${n} doctors in your focus are no longer shared.`,
      explica: 'You only get alerts for conversations of the checked doctors.',
      soEste: (nome: string) => `Only ${nome}`,
      tirarSoEste: 'Back to your focus',
    },
    da: (nome: string) => `from ${nome}`,
    com: (nome: string) => `with ${nome}`,
    voce: 'You',
    assumir: 'Take',
    assumirDe: (nome: string) => `Take from ${nome}`,
    soltar: 'Release',
    assumirDica: 'It stays with you: others see this conversation has an owner.',
    escrevendo: (nomes: readonly string[]) =>
      nomes.length === 1 ? `${nomes[0]} is typing` : `${nomes.join(', ')} are typing`,
    searchPlaceholder: 'Search by name, phone or doctor',
    filters: {
      all: 'All',
      awaiting: 'Awaiting',
      urgent: 'Urgent',
      resolved: 'Resolved',
      mine: 'Mine',
      unassigned: 'Unassigned',
    },
    allClients: 'All clients',
    allClientsShort: 'All',
    clientsChosen: (n: number) => `${n} clients`,
    emptyTitle: 'Nothing here',
    emptyText: 'No conversation matches this filter. Try "All", or go back to every client.',
    pickTitle: 'Pick a conversation',
    pickText:
      'The list on the left brings together patients from all your clients, each tagged with who they belong to.',
    naoSuportada: 'WhatsApp does not pass this message type on. Check the practice phone.',
    noMessages: 'No messages',
    waitingFor: (tempo: string) => `waiting for ${tempo}`,
    windowClosed: 'window closed',
    windowClosingIn: (tempo: string) => `closes in ${tempo}`,
    resolve: 'Resolve',
    reopen: 'Reopen',
    reopenHint: 'Put this conversation back in the queue: it stops being resolved.',
    reply: 'Reply',
    internalNote: 'Internal note',
    send: 'Send',
    replyTo: (paciente: string, cliente: string) => `Reply to ${paciente} on behalf of ${cliente}`,
    failed: 'Failed',
    internalNoteLabel: 'Internal note',
    sentFromPhone: "sent from the doctor's phone",
    attach: 'Attach a file',
    download: 'Download',
    mediaExpires: (data: string) => `available until ${data}`,
    tagAdd: 'Tag',
    tagSearch: 'Search or create…',
    tagCreate: (nome: string) => `Create “${nome}”`,
    tagSearchOnly: 'Search…',
    tagSoDaDona: 'No tag with that name. Only whoever brought the doctor creates new tags.',
    tagFirst: 'Type to create your first tag.',
    tagAllUsed: 'Every tag is already on this conversation.',
    panelToggle: 'Contact details',
    conversation: 'Conversation',
    conversationEmpty: 'Conversation with no messages',
    conversationEmptyText:
      'Nobody has written yet. To start, send an approved template below, or wait for the patient to write.',
    filterByClient: 'Filter by client',
    contactCard: 'Contact details',
    collapseCard: 'Collapse contact details',
    expandCard: 'Open contact details',
    client: 'Client',
    assignee: 'Assigned to',
    noAssignee: 'Unassigned',
    lastMessage: 'Last message',
    saveNote: 'Save note',
    blockedByWindow: 'Free-form replies blocked by the 24-hour window',
    windowTooltip: '24-hour window closed: only an approved template reopens the conversation',
    noteTooltip: 'Internal note: never goes to the patient’s WhatsApp',
    window: {
      closedTitle: 'Window closed: template only',
      closedReason:
        'The patient has not written in over 24 hours, and Meta only allows reopening with an approved template. If the number uses Coexistence, you can also reply from WhatsApp Business on the phone.',
      openTitle: 'Window open',
      freeTitle: 'Free entry point',
      remaining: (tempo: string) => `, ${tempo} left`,
      freeSub: 'Conversation started from an ad: 72 hours, and everything in it is free.',
      openSub: (hora: string) => `Free-form replies allowed until ${hora}.`,
      replyNow: 'reply now',
    },
    today: 'Today',
    yesterday: 'Yesterday',
    hint: {
      comVoce: 'You took this conversation',
      comAlguem: (nome: string) => `${nome} took this conversation`,
      resolved: 'Conversation resolved: nothing pending here',
      locked: '24-hour window closed: only an approved template reopens the conversation',
      open: 'Conversation open: free-form replies allowed',
      needsReply: 'The patient wrote and nobody has replied yet',
      unread: (n: number) => (n === 1 ? '1 unread message' : `${n} unread messages`),
      waiting: (tempo: string) => `The patient has been waiting ${tempo} for a reply`,
      closingIn: (tempo: string) =>
        `The 24-hour window closes in ${tempo}: after that, template only`,
      tag: (nome: string) => `Tag: ${nome}`,
      moreTags: (n: number) => `${n} more tag(s) on this conversation`,
      stamp: (quando: string) => `Last message on ${quando}`,
    },
    windowOpenFlag: 'window open',
    windowClosingIn2: (tempo: string) => `closes in ${tempo}`,
    windowTitle: '24-hour window',
    windowOpenFor: (tempo: string) => `open · ${tempo} left`,
    windowShut: 'closed',
    nextAppointment: 'Next appointment',
    noAppointment: 'No appointment scheduled.',
    telehealth: 'Telehealth',
    clientTag: (nome: string) => `Client: ${nome}`,
  },

  clients: {
    newClient: 'New client',
    emptyTitle: 'No clients yet',
    emptyText:
      'Add the first doctor or clinic you work for to start receiving their conversations in the inbox.',
    columns: {
      client: 'Client',
      connection: 'Connection',
      conversations: 'Open conversations now',
      patientsMonth: 'Patients this month',
      billing: 'Billing',
      openClient: 'Open client',
    },
    searchPlaceholder: 'Search by name or specialty',
    noMatchTitle: 'No client found',
    noMatchText: (termo: string) => `Nothing matches “${termo}”. Try another name.`,
    revenueFooter: (n: number) =>
      n === 1 ? 'Monthly revenue · 1 active client' : `Monthly revenue · ${n} active clients`,
    onAir: 'Online',
    onAirHint: (n: number) =>
      n > 1
        ? `All ${n} numbers for this client are connected and sending normally.`
        : "This client's WhatsApp is connected and sending normally.",
    numbersOnAir: (n: number) => `${n} numbers online`,
    overdue: 'OVERDUE',
    onTime: 'paid',
    pending: 'pending',
    noDay: 'no date',
    dayOf: (d: number) => `day ${d}`,
    overdueHint: 'Payment overdue',
    onTimeHint: 'Paid: nothing outstanding',
    pendingHint: 'Payment outstanding, not due yet',
    pendingHintOn: (d: number) => `Payment outstanding: due on day ${d}`,
    tabs: {
      overview: 'Overview',
      patients: 'Patients',
      templates: 'Templates',
      billing: 'Billing',
      activity: 'Activity',
    },
    contact: 'Contact',
    faleCom: (nome: string) => `${nome} is the one who can fix it: let them know.`,
    daEquipe: 'From your teams',
    pelaEquipe: (nome: string) => `Via team, from ${nome}`,
    soDaDona: (nome: string) =>
      `This doctor's number, record and billing stay with ${nome}. You handle their conversations and schedule.`,
    phone: 'Phone',
    since: 'Client since',
    seeConversations: 'See conversations',
    connection: 'Connection',
    connectionSub: "This doctor's WhatsApp. It is what breaks on its own and you have to restore.",
    noDoctorsTitle: 'No doctors registered',
    noDoctorsText: "Add this client's doctor to connect their WhatsApp.",
    noNumber: 'No number connected',
    noNumberNote:
      'This doctor neither receives nor replies through Zenda yet. Until their WhatsApp is connected, none of their conversations reach your inbox.',
    connectWhatsapp: 'Connect WhatsApp',
    reconnect: 'Reconnect',
    channelType: 'Channel type',
    coexistence: 'Coexistence',
    classicChannel: 'Classic channel',
    quality: 'Quality',
    metaBilling: 'Billing at Meta',
    tier: 'Sending tier',
    perDay: '/ day',
    neverSynced: 'Quality and limit not yet checked with Meta',
    syncedAt: (quando: string) => `Checked with Meta ${quando}`,
    syncNow: 'Check now',
    disconnect: 'Disconnect number',
    disconnectConfirm: 'Confirm disconnect',
    disconnecting: 'Disconnecting…',
    disconnectHelp:
      'The number stops sending and receiving through Zenda, and the conversations stay. To connect again, create a new link.',
    billingByZenda: 'Paid by Zenda',
    noTierLimit: 'No cap',
    appSignal: 'App signal',
    notApplicable: 'Not applicable',
    noSignal: 'No signal recorded',
    activeToday: 'Active today',
    daysAgo: (n: number) => `${n} ${n === 1 ? 'day' : 'days'} ago`,
    billingTitle: 'Billing',
    billingSub:
      'A billing reminder. Zenda issues nothing and charges nothing: the control is yours.',
    noBilling: 'No charges recorded yet.',
    markPaid: 'Mark as received',
    undoPaid: 'Undo',
    received: 'Received',
    dueOn: (data: string) => `due ${data}`,
    paidOn: (data: string) => `paid ${data}`,
    volumeTitle: 'Volume',
    volumeSub: 'How much work this client brings.',
    patientsTotal: 'Patients in total',
    trendFlat: 'Same as last month',
    trendPct: (p: number, subiu: boolean) =>
      subiu ? `+${p}% vs. last month` : `-${p}% vs. last month`,
    trendFromZero: (n: number) => `${n} this month: last month was zero`,
    trendNoData: 'No activity in either month',
    trendFoot: (antes: number, agora: number) =>
      `Last month ${antes} · this month ${agora}, still running`,
    patientsMonth: 'Patients this month',
    openConversations: 'Open conversations',
    appointmentsMonth: 'Appointments this month',
    sectionsAria: 'Client sections',
    openError: 'Could not open this client',
    openBadge: 'Open',
  },

  clientForm: {
    specialtyPlaceholder: 'Dermatology',
    newTitle: 'New client',
    newSubtitle: 'A doctor you work for. Once registered, you connect their WhatsApp.',
    editTitle: (nome: string) => `Edit ${nome}`,
    editSubtitle: "The doctor's WhatsApp is managed on the client's page.",
    submitNew: 'Add client',
    submitEdit: 'Save changes',
    name: 'Client name',
    namePlaceholder: 'Dr. Beatriz Nogueira',
    nameRequired: 'Give the client a name: it is how they appear in conversations.',
    specialty: 'Specialty',
    // Brazil's medical licence number. Kept as-is: it is a proper noun on a Brazilian
    // register, and translating it would make it unsearchable.
    crm: 'CRM (medical licence)',
    contactName: 'Contact person',
    contactHint: 'Who calls you when they need something',
    contactPhone: 'Contact phone',
    fee: 'What you charge per month (R$)',
    day: 'Billing day',
    dayHint:
      'Just a reminder so you do not miss the date: Zenda issues nothing and charges nothing. Up to the 28th, because of February.',
    dayInvalid: (max: number) =>
      `The billing day must be between 1 and ${max}: because of February.`,
    paymentOwner: 'Who pays Meta for this client',
    ownerClient: 'They do',
    ownerAttendant: 'You do',
    ownerHintClient:
      "The doctor attaches their own card to their own Meta account. It is the default path, and the only one where you don't carry their credit risk.",
    agendaSource: "This client's calendar",
    agendaZenda: 'Zenda',
    ownerHintZenda:
      'Zenda, through Gupshup. The doctor adds no card, and you do not pay Meta for them.',
    agendaDoctoralia: 'Doctoralia',
    agendaHintZenda: 'The calendar is ours: we guarantee two patients never land on the same slot.',
    agendaHintDoctoralia:
      'Doctoralia becomes the source of truth, and Zenda becomes its interface. Booking here reserves there first, and whatever the patient books there shows up here on its own. All that stays from Zenda is availability, saying when booking is allowed.',
    ownerHintAttendant:
      'They add you to their portfolio as an administrator and you attach your card, folding the cost into your fee. The account and the number remain theirs.',
    status: 'Status',
    statusActive: 'Active',
    statusPaused: 'Paused',
    statusChurned: 'Ended',
    statusHintActive:
      'Active is the normal state: counts towards monthly revenue and dashboard alerts.',
    statusHintPaused:
      'Paused stays on the list but stops generating charges. For someone on holiday.',
    statusHintChurned:
      'Ended drops out of monthly revenue and the dashboard. Conversations and history stay here.',
  },

  patients: {
    title: 'Patients',
    subtitle:
      'Everyone who has messaged this client, plus the ones you added. The number identifies the person: when they write, the message lands on the same record.',
    searchPlaceholder: 'Search by name or number',
    searchAria: 'Search patient',
    dueFilter: 'Due back',
    emptyTitle: 'No patients yet',
    emptyText:
      "Add a patient, or wait: anyone who messages this client's WhatsApp shows up here on their own.",
    notFoundTitle: 'Nothing found',
    notFoundText: 'Try another term, or turn off the return filter.',
    columns: {
      patient: 'Patient',
      nextAppointment: 'Next appointment',
      lastConversation: 'Last conversation',
      appointments: 'Appointments',
      openRecord: 'Open record',
    },
    noReturn: 'no return set',
    conversations: 'Conversations',
    appointments: 'Appointments',
    patientSince: 'Patient since',
    notesTitle: 'Notes',
    notesPlaceholder: 'Prefers mornings. Always comes with her daughter. Insurance X.',
    notesHint:
      'Only you and your team see this. It never appears to the patient, never goes into a message: and notes for one client never show on another one.',
    birthDate: 'Date of birth',
    appointmentsNone: 'No appointments recorded.',
    conversationsNone: 'Has not messaged on WhatsApp yet.',
    consultation: 'Appointment',
    openConversation: 'Open conversation',
    sendMessage: 'Send message',
    autoSaved: 'Changes save themselves.',
    saved: 'Saved',
    intervalInvalid: 'The interval must be greater than zero',
    patientFallback: 'Patient',
    newPatient: 'New patient',
    neverTalked: 'Has not messaged yet',
    novo: {
      title: 'New patient',
      subtitle:
        'WhatsApp identifies the person. When they write, the message lands on this record.',
      name: 'Name',
      namePlaceholder: 'Maria da Silva',
      phone: 'WhatsApp',
      phonePlaceholder: '(11) 98765-4321',
      nameRequired: "Enter the patient's name.",
      phoneRequired: "Enter the patient's WhatsApp number.",
      phoneHint:
        'With area code. Landline or mobile, with or without the extra 9: both are recognized.',
      phoneOk: (n: string) =>
        `It will be saved as ${n}. When this number writes on WhatsApp, the message lands in this record.`,
      jaCadastrado: (nome: string) =>
        `This number already belongs to ${nome}. Saving opens the existing record, with no duplicate.`,
      submit: 'Add patient',
      alreadyLinked: 'This number is already a patient of this client. We opened their record.',
    },
    ficha: {
      grupos: {
        identificacao: 'Identification',
        contato: 'Contact',
        endereco: 'Address',
        convenio: 'Insurance',
        origem: 'Source',
      },
      campos: {
        nome: 'Name',
        whatsapp: 'WhatsApp',
        socialName: 'Preferred name',
        sex: 'Sex',
        cpf: 'CPF',
        email: 'Email',
        phoneSecondary: 'Other phone',
        cep: 'Postal code',
        street: 'Street',
        streetNumber: 'Number',
        complement: 'Apt / unit',
        district: 'District',
        city: 'City',
        uf: 'State',
        insurer: 'Insurer',
        insurancePlan: 'Plan',
        insuranceCardNumber: 'Card number',
        insuranceValidUntil: 'Valid until',
        referralSource: 'How they found the doctor',
      },
      sexos: { F: 'Female', M: 'Male', I: 'Other' },
      naoInformado: 'Not informed',
      nomeObrigatorio: 'The name cannot be empty.',
      telefoneObrigatorio: 'The WhatsApp number identifies the patient and cannot be empty.',
      telefoneTravado:
        'The patient has already messaged from this number, so it can no longer change.',
    },
    dependentes: {
      title: 'Dependents',
      hint: 'People seen at the appointment who are not the WhatsApp owner, like a child. Messages still go to the guardian.',
      none: 'No dependents.',
      add: 'Add dependent',
      nameRequired: 'Enter the dependent’s name.',
      name: 'Name',
      relationship: 'Relationship',
      birthDate: 'Date of birth',
      save: 'Save',
      edit: 'Edit',
      archive: 'Archive',
      unarchive: 'Restore',
      archived: 'Archived',
      archiveWarning: (n: number) =>
        n === 1 ? 'Has 1 upcoming appointment.' : `Has ${n} upcoming appointments.`,
      parentescos: {
        filho: 'Son or daughter',
        enteado: 'Stepchild',
        neto: 'Grandchild',
        sobrinho: 'Nephew or niece',
        tutelado: 'Ward',
        conjuge: 'Spouse',
        pai_mae: 'Parent',
        outro: 'Other',
      },
      paraQuem: 'For whom',
      oProprio: (nome: string) => `${nome} (the patient)`,
      deQuem: (dependente: string, responsavel: string) =>
        `${dependente} · guardian ${responsavel}`,
      proximaDe: (nome: string) => `for ${nome}`,
      naLista: (nomes: string) => `Dependents: ${nomes}`,
    },
    optIn: {
      title: 'WhatsApp messages',
      declare: 'The patient agreed to receive messages from this doctor on WhatsApp',
      method: 'How they agreed',
      methods: {
        presencial: 'In person',
        telefone: 'By phone',
        papel: 'In writing',
        outro: 'Other',
      },
      hint: 'Without this, the first message can only go to someone who has already written.',
      precisa:
        'To send the first message, record under "WhatsApp messages" that the patient agreed.',
      given: (quando: string) => `Agreed on ${quando}`,
      none: 'No agreement recorded.',
      register: 'Record agreement',
      remove: 'Remove',
    },
  },

  agenda: {
    dias: {
      letra: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      curto: ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
      titulo: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    },
    deOutrosMedicos: 'Patients of other doctors (they become this doctor’s too)',
    consulta: {
      abrirConversa: 'Open the conversation',
      abrirFicha: "Open the patient's record",
      teleconsulta: 'Telehealth',
      encaixe: 'squeezed in',
      marcadaPor: (nome: string) => `Booked by ${nome}`,
      alteradaPor: (nome: string) => `changed by ${nome}`,
      naPlataforma:
        'This appointment lives in Doctoralia. Rescheduling, confirming, attendance and cancelling apply there too.',
      remarcar: 'Reschedule',
      novaData: 'New date',
      novaHora: 'New time',
      salvarRemarcacao: 'Reschedule to this time',
      voltar: 'Back',
      confirmar: 'Patient confirmed',
      veio: 'Attended',
      faltou: 'No-show',
      desmarcar: 'Cancel',
      desmarcarPergunta: 'Cancel this appointment?',
    },
    chooseTitle: 'Whose calendar?',
    chooseText: 'Pick a client to see the days, the hours and what is booked.',
    noDoctor: 'No doctor',
    noDoctorText:
      'This client has no doctor on file, and a calendar belongs to a doctor. Add one on the client page.',
    periods: {
      madrugada: 'Overnight',
      manha: 'Morning',
      tarde: 'Afternoon',
      noite: 'Evening',
    },
    noService: 'no hours',
    previousDay: 'Previous day',
    nextDay: 'Next day',
    secoes: 'Schedule sections',
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    jumpToDate: 'Jump to a date',
    backToClients: 'Switch client',

    today: 'Today',
    week: 'Week',
    tabCalendar: 'Calendar',
    month: 'Month',
    visao: 'Calendar view',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    verDia: 'Open the day',
    maisN: (n: number) => `+${n} more`,
    tabDay: 'Day',
    tabAvailability: 'Availability',
    tabIntegration: 'Integration',

    telehealth: 'Telehealth',
    free: 'free',
    extra: 'extra',
    closedNoRoutine: 'not working',
    closedBlocked: 'blocked',
    booked: (n: number) => (n === 1 ? '1 booked' : `${n} booked`),

    closedDayTitle: 'The doctor does not work on this day',
    closedDayText: 'The weekly routine opens no slot here. An "extra hours" exception opens one.',
    blockedDayTitle: 'Day blocked',
    sourceZendaTitle: "This client's calendar is Zenda",
    sourceZendaText:
      'Nothing to sync: we are the calendar, and our own constraint is what guarantees two patients never land on the same slot. To use Doctoralia, change it on the client record.',
    noLinksTitle: 'The calendar is Doctoralia, and nothing is linked yet',
    noLinksText:
      'Booking from here is off until you link one: the appointment is created there first, and with no linked calendar there is nowhere to create it. Link one below, or switch this client back to the Zenda calendar on their page.',
    linksTitle: 'Linked calendars',
    syncFailed: 'failed',
    willRetry: 'will retry on its own',
    neverSynced: 'not synced yet',
    syncedAt: 'synced',

    configOpen: 'Configure',
    configTitle: 'Calendar system',
    configSystem: 'Which system is this client’s calendar',
    configSystemHint:
      'On a third-party platform the source of truth moves there and Zenda becomes the interface. What is left of the availability here is the guardrail for when booking is allowed.',
    configZendaDesc:
      'The calendar is ours, and our own constraint is what keeps two patients out of the same slot.',
    configPlatformDesc:
      'Both sides move together: what you book here is created there first, and whatever the patient books there shows up here on its own.',
    configConnection: 'The connection',
    configSimulated: 'Simulated',
    configLive: 'Live',
    configSimulatedHint:
      'Talks to nobody: it exists so the flow can be seen working before the partnership is approved.',
    configLiveHint:
      'Talks to the real platform. Access is granted through an approved partnership and authorized client by client.',
    configKeepSecret: 'Blank keeps the current credential',
    configRedirected:
      'Heads up: Live mode is redirected and does NOT reach the platform. Calls go to',
    configRedirectedBadge: 'Live, redirected',
    configRead: 'Read calendars',
    configReading: 'Reading…',
    configTelehealth: 'Telehealth (online calendar)',
    configMapping: 'Which location maps to which calendar',
    configMappingHint:
      'Over there the calendar IS the address, and nothing guarantees the names line up. Without saying which maps to which, one address’s grid would go to another’s calendar.',
    configDefault: 'default',
    configNoService:
      'Nothing configured on the platform for this field, and without a value there is no booking.',
    configDefaults: 'Booking defaults',
    configHere: 'Here (Zenda)',
    configThere: 'There',
    configNoSync: 'Do not sync',
    configDisabled: 'disabled there',
    configDisabledWarn: 'That calendar is disabled on the platform and will refuse bookings.',
    configNoCalendars: 'That account has no calendars. Check the platform dashboard.',
    configNoDoctor:
      'This client has no doctor on record, and the calendar over there belongs to a doctor.',
    linksHint:
      'One calendar per address, because over there the calendar IS the address. Sync runs in real time both ways and has no button: what shows up here is whether something failed.',
    mirrorOf: 'Doctoralia',
    pushShow: 'Push URL',
    pushHint:
      'Paste it in the platform dashboard, in the notification field. Without it, a booking made there only shows up here on the next pull, up to a minute later. It carries a secret: treat it like a password.',
    pushMissing: 'This link has no push URL. Save the configuration again to generate one.',
    mirrorHint:
      "This doctor's calendar is Doctoralia. What you see here is what exists there, and booking here reserves there first.",
  },

  templatesTab: {
    title: 'Message templates',
    subtitle:
      "A ready message approved by Meta. It is the only way to write to someone silent for more than 24 hours, as in an appointment reminder. Each template belongs to this doctor's account.",
    newTemplate: 'New template',
    newTemplateSubtitle: (cliente: string) =>
      `Goes to Meta for review on ${cliente}'s account. The answer usually takes minutes.`,
    colMessage: 'Message',
    colName: 'Name',
    colLanguage: 'Language',
    colCategory: 'Category',
    colStatus: 'Status',
    open: 'Open',
    sentText: 'Text submitted to Meta',
    sentExamples: 'Examples submitted',
    rejectedReason: (motivo: string) => `Rejection reason reported by Meta: ${motivo}`,
    unavailableTitle: 'Templates unavailable',
    emptyTitle: 'No templates yet',
    emptyText:
      'Without an approved template you can only reply to people who wrote in the last 24 hours. Start with a utility one. Example: "Hi {{1}}, your appointment is tomorrow at {{2}}. Confirm?"',
    status: {
      approved: 'Approved',
      approvedHelp: 'Can be sent, including outside the 24-hour window.',
      pending: 'In review',
      pendingHelp: 'Meta is still reviewing. It usually takes minutes, but it can take hours.',
      rejected: 'Rejected',
      rejectedHelp:
        'Meta rejected it. Fix the text and create it again, a rejected template never becomes valid.',
      paused: 'Paused',
      pausedHelp: 'Paused for low quality: too many patients blocked or reported it.',
      unknownHelp: 'Status reported by Meta.',
    },
    categories: {
      UTILITY: 'Utility',
      MARKETING: 'Marketing',
      AUTHENTICATION: 'Authentication',
    },
    reasons: {
      sem_canal:
        'This client has no WhatsApp connected yet. Templates live inside their Meta account, so they only show up after the connection.',
      sem_credencial:
        'This channel credential is not available. Reconnect the doctor WhatsApp to read the templates again.',
      meta_indisponivel: 'Could not reach Meta right now. Try again in a moment.',
      sem_meta:
        'This environment is not connected to Meta, so there are no templates to read or create here. They show up normally in staging and production.',
    },
  },

  templateForm: {
    fromModel: 'Start from a draft',
    yourModels: 'Your drafts, from Settings → Template drafts.',
    internalName: 'Internal name',
    internalNameHint: 'Only your team sees it. The patient never reads this name.',
    internalNamePlaceholder: 'reminder_appointment_24h',
    message: 'Message',
    messageHint: (n: number) => `Use {{1}}, {{2}} for what changes per patient. ${n}/1024`,
    messagePlaceholder: 'Hi {{1}}, your appointment is booked for {{2}} at {{3}}.',
    examples: 'An example for each variable',
    examplesHint:
      'Meta uses them to review. Use made-up examples, like "Ana" and "tomorrow at 9am": the text shows on the patient\'s lock screen.',
    exampleFirst: 'Ana',
    exampleAria: (n: number) => `Example for variable ${n}`,
    onNotification: 'On the notification',
    onNotificationHint:
      'The patient reads this without unlocking, and the phone is usually shared.',
    previewEmpty: 'Your message shows up here.',
    onConversation: 'In the conversation',
    zendaRule: 'Zenda rule',
    categoryPre: 'Submitted as',
    categoryUtility: 'Utility',
    categoryPost:
      'but Meta decides, based on the text. If it reads as promotion, it becomes Marketing, which costs about 9 times more per message (R$ 0.32 vs R$ 0.035).',
    sending: 'Sending…',
    submit: 'Submit for review',
    rules: {
      nome: () =>
        'The name takes only lowercase letters, numbers and underscores. E.g. reminder_appointment_24h',
      corpo_vazio: () => 'Write the message text.',
      corpo_tamanho: (d: Vars) =>
        `The text is over ${d['limite']} characters. Cut ${d['excesso']}.`,
      rodape_tamanho: () => 'The footer takes up to 60 characters.',
      cabecalho_tamanho: () => 'The header takes up to 60 characters.',
      chaves: () =>
        'There is a lone brace in the text. A variable is written exactly like this: {{1}}',
      numeracao: (d: Vars) => `Variables must be 1, 2, 3… with no gaps. Found: ${d['achadas']}`,
      variavel_no_inicio: () =>
        'The text cannot start with a variable. Start with a word, e.g. "Hi {{1}}".',
      variavel_no_fim: () =>
        'The text cannot end with a variable. Close with a word or punctuation.',
      exemplos: (d: Vars) => `Fill in an example for each variable. ${d['faltando']} missing.`,
      muita_variavel: () =>
        'Almost all of the text is variable. Meta usually rejects messages like this, so write more fixed text.',
      dado_clinico: (d: Vars) =>
        d['onde'] === 'nome'
          ? `Remove "${d['termo']}" from the name. The name never reaches the patient, but it travels to Meta and is read by a human reviewer, and it gives away the doctor specialty just the same.`
          : `Remove "${d['termo']}" from the text. The text shows on the patient lock screen, before they unlock, and the phone is shared with family and work.`,
      tom_promocional: () =>
        'Promotional wording makes Meta switch the template to Marketing, which costs about 9 times more per message. Replace words like "promotion" or "discount" with a direct notice.',
    },
  },

  connect: {
    titleConnect: (medico: string) => `Connect ${medico}'s WhatsApp`,
    titleReconnect: (medico: string) => `Reconnect ${medico}'s WhatsApp`,
    subtitleReconnect:
      'The connection dropped. Restoring it requires the doctor to re-authorise from their own device.',
    subtitleClient:
      'The number belongs to the doctor and stays theirs: so does the Meta account. You guide, they authorise.',
    subtitleAttendant:
      'The number and the account stay theirs. They add you as an administrator at the end and YOU attach your card.',
    start: 'Start connection',
    startReconnect: 'Reconnect now',
    starting: 'Starting…',
    whatHappens: 'Step by step of what the doctor will do',
    custosTitulo: 'Card, 24-hour window and what Meta charges',
    simulated: 'Simulated connection.',
    testNumber: "Meta's test number.",
    openMeta: 'Open the Meta flow',
    linkTitle: 'Is the doctor somewhere else?',
    linkHelp:
      'Generate a link and send it over. They open it on their own computer, sign in with their own Meta account and authorize. Valid for 72 hours, single use.',
    linkCreate: 'Generate a link for the doctor',
    linkCreating: 'Generating…',
    linkCopy: 'Copy link',
    linkCopied: 'Copied',
    linkExpires: (quando: string) => `Valid until ${quando}. Generating another one voids this.`,
    metaRunning: 'Waiting for Meta…',
    metaStep: (passo: string) => `In the Meta window, step: ${passo}`,
    metaDone: 'Number connected.',
    metaHalfDone: 'Connected, but no webhook.',
    checklistTitle: 'Before calling the doctor, confirm:',
    sendBefore: 'Send this to the doctor before calling',
    openWhatsapp: 'Open in WhatsApp',
    copyBlocked: 'The browser blocked copying. Select the text below and copy it manually.',
    noPhone:
      'No contact phone registered for this client: that is why the conversation cannot be opened directly. Copy the text, or add the phone number to the client.',
    metaCardHelp: "Meta's step-by-step for adding a card",
    metaAddPeople: 'How they add you to their portfolio',
    metaBilling: 'Go straight to billing settings',
    whyCardTitle: 'If they ask why the card is theirs',
    whyAccessTitle: 'If they ask why you need access',
    whyCard: [
      'The Meta account sits under their own company, and the WhatsApp number stays theirs: if they ever leave, they leave with it.',
      'They can see their own spend at any time, with no middleman and no markup.',
      'You do not charge per message: you charge for your service, which is a separate bill.',
      "It is Meta's rule for our type of partnership, not our choice: with no card on their account, the number connects and does not send.",
    ],
    whyAccess: [
      'The account and the number stay theirs, under their own company: you come in as an administrator, not as an owner.',
      'They can remove you themselves at any moment and keep everything. Meta does not even let us restrict their access.',
      'Without that access you cannot attach your card: and with no card the number connects and does not send.',
      'A bonus: display name, quality and policy issues become yours to resolve, without calling them every time.',
    ],
    semCartao: {
      subtitle:
        "The number and the account stay the doctor's. They authorize through the link and add no card: Zenda pays for the messages.",
      custosTitulo: 'The 24-hour window and what Meta charges',
      linkHelp:
        'Send the link to the doctor. They see a short walkthrough and connect from phone or computer in about 5 minutes. Valid for 72 hours, single use.',
      conferir: 'Check whether the doctor finished',
      conferindo: 'Checking…',
      conferido: {
        ativado: 'Number connected. Conversations are now coming into Zenda.',
        ja_ativo: 'Number connected. Conversations are now coming into Zenda.',
        pendente: 'The doctor has not finished yet. Check again when they tell you.',
        parado:
          'The doctor reached the end, but the account was not activated. It is almost always a card from an old connection: see "If something fails" below.',
        sem_convite: 'Create the link first and send it to the doctor.',
      },
      guia: {
        antesItens: [
          'Number on the WhatsApp Business app, up to date, with the phone nearby. Meta rejects regular WhatsApp.',
          'The doctor can log in to Facebook. It is the login that stalls most.',
          'The number was never connected to another platform with a card. If it was, read "If something fails" before sending the link.',
        ],
        passos: [
          {
            titulo: 'Opens the link',
            texto:
              'Sees a 6-screen walkthrough and taps "Connect my WhatsApp". Goes to Gupshup, the Meta partner that pays for messages on Zenda\'s behalf.',
          },
          {
            titulo: 'Logs in with Facebook',
            texto: 'Taps "Continue with Facebook" and, in Meta\'s window, "Continue".',
          },
          {
            titulo: 'Types the practice number',
            texto:
              'Under "Add your WhatsApp phone number", keeps "Enter a new phone number" and types the WhatsApp Business number. Checks name and photo and continues. The portfolio is preselected.',
          },
          {
            titulo: 'Scans the QR code',
            texto:
              'In WhatsApp Business, taps "Connect" on the Facebook Business message (or Settings › Account › Business Platform) and agrees to share the history.',
          },
          {
            titulo: 'Done',
            texto:
              'Gupshup shows "Congratulations". Within seconds the number shows as connected here. If not, tap "Check whether the doctor finished".',
          },
        ],
        alerta: 'There is no card step. If Meta offers "Add payment method", skip it: Zenda pays.',
        depois: 'After connecting, the doctor keeps using WhatsApp Business on the phone as usual.',
      },
      erros: {
        titulo: 'If something fails',
        itens: [
          {
            titulo: '"Unable to add credit line to WABA"',
            texto:
              'The number already had a card from an old Meta connection, and Meta does not swap the card on that account. Fix: the doctor backs up the chats, deletes and recreates the WhatsApp Business account, restores the backup and connects again. Note: a recreated account hits the error below for a few days.',
          },
          {
            titulo: '"Your phone number isn\'t eligible"',
            texto:
              'Meta requires real WhatsApp Business usage before connecting, and a new account has none. It cannot be sped up: the doctor uses the app normally for a few days and tries again. Recreating the account again resets the count.',
          },
          {
            titulo: 'Any other error',
            texto:
              "Ask for a screenshot. Meta's message carries a code in parentheses, for example (#3441045), and that code says what happened.",
          },
        ],
      },
      custosNota:
        'Per delivered message in Brazil, Meta rate card from 10/01/2026. Zenda pays, through Gupshup.',
      checklist: {
        login: 'Can they log in to Facebook right now? It is the step that stalls most.',
        phone: 'Is the number on an up-to-date WhatsApp Business, with the phone at hand?',
        identity: 'Is the name patients will see already decided?',
        historico:
          'Was the number never connected to another platform with their card? If it was, see "If something fails" first.',
      },
      inviteAbertura: (primeiro: string) =>
        `Hi, ${primeiro}! For me to answer your patients on the practice WhatsApp, I need you to connect the number. It takes about 5 minutes, and the link explains each step.`,
      inviteFecho: (atendente: string) =>
        `I will send you the link next. If you prefer, we can do it together over the phone.

${atendente}`,
      invite: [
        'First, please check 2 things:',
        '',
        '1) Can you log in to your Facebook now? If you lost the password, recover it first.',
        '2) Is the number on the WhatsApp Business app, up to date, with the phone nearby? Regular WhatsApp does not work.',
        '',
        'You add no card: Zenda covers the message costs. The number and the account stay yours, and you can disconnect anytime.',
        '',
        'If this number was ever connected to a platform with your card, tell me first: there is one extra step.',
      ],
      carrossel: {
        passoDe: (i: number, n: number) => `${i} of ${n}`,
        navegacao: 'Walkthrough screens',
        anterior: 'Back',
        proximo: 'Next',
        pular: 'I know the steps, connect now',
        conectar: 'Connect my WhatsApp',
        indo: 'Opening the connection…',
        rodape:
          'You go to Gupshup and log in with your Facebook. Zenda never sees or stores your password.',
        telas: (operacao: string) => [
          {
            titulo: 'What will happen',
            texto: `${operacao} will answer your patients on the practice WhatsApp. You keep using WhatsApp Business on your phone, with the same chats.`,
            itens: [
              'It takes about 5 minutes.',
              'Keep the practice phone nearby.',
              'You will log in with your Facebook.',
            ],
          },
          {
            titulo: 'Who Gupshup is',
            texto:
              'The connection goes through Gupshup, an official partner of Meta, the owner of WhatsApp. That is how Zenda pays for the messages: you add no card.',
            itens: [
              'The number and the account stay yours.',
              'You can disconnect anytime, in WhatsApp Business itself.',
            ],
          },
          {
            titulo: 'Step 1: log in with Facebook',
            texto:
              'On the Gupshup page, tap "Continue with Facebook" and log in with the account that manages the practice. In Meta\'s window, tap "Continue".',
          },
          {
            titulo: 'Step 2: type the number',
            texto:
              'Under "Add your WhatsApp phone number", keep "Enter a new phone number" and type the practice number, the same one as WhatsApp Business.',
            itens: [
              'Meta shows the profile name and photo. Check them and tap "Next".',
              'The portfolio is preselected. Tap "Next" again.',
            ],
          },
          {
            titulo: 'Step 3: scan the QR code',
            texto:
              'On the phone, open WhatsApp Business and tap "Connect" on the Facebook Business message. Then scan the QR code on screen.',
            itens: [
              'Cannot find the message? Settings › Account › Business Platform.',
              'Agree to share the history: that is how chats show up for the assistant.',
              'When Gupshup shows "Congratulations", you are done.',
            ],
          },
          {
            titulo: 'If an error shows up',
            texto: `Do not retry many times. Take a screenshot and send it to ${operacao}.`,
            itens: [
              '"Unable to add credit line to WABA": the number already has a card from an old Meta connection. There is one extra step, and the assistant explains it.',
              '"Your phone number isn\'t eligible": Meta needs more WhatsApp Business usage before connecting. Use it normally for a few days.',
            ],
          },
        ],
      },
    },
    guia: {
      antes: 'Before you start',
      antesItens: [
        'The number must be on the WhatsApp Business app, up to date, on the practice phone. On the regular WhatsApp app, Meta rejects the number and says it is already registered.',
        'Log in to Facebook with the account that manages the practice.',
        'Have a valid credit or debit card and the practice tax ID (CPF or CNPJ) at hand.',
      ],
      passos: [
        {
          titulo: 'Open the link and tap "Connect my WhatsApp"',
          texto: 'A Meta window opens. On "Easily connect your account to Zenda", tap Continue.',
        },
        {
          titulo: 'Connect the existing WhatsApp Business account',
          texto:
            'Pick the existing account option and type the practice number. Meta shows the profile name and photo: check them and tap Next.',
        },
        {
          titulo: 'Choose the business portfolio',
          texto:
            'Use the practice portfolio, or create one. If Meta says the portfolio limit was reached, pick an existing one.',
        },
        {
          titulo: 'Scan the QR code with WhatsApp Business',
          texto:
            'On the phone, open WhatsApp Business, find the message from the official Facebook Business account and tap Connect. If it is not there, open the app menu or settings and scan the QR code on screen. Then choose whether to share the last 6 months of history.',
        },
        {
          titulo: 'Confirm the account name and time zone',
          texto:
            'Keep the time zone at (GMT-03:00) America/Sao_Paulo and tap Next. Wait for "Connecting your account" to finish.',
        },
      ],
      passoCartaoMedico: {
        titulo: 'Add the card now. Do not skip this step',
        texto:
          'On "Your account is connected", tap "Add payment method", not Done. Choose credit or debit card and fill it in. Then enter the CPF or CNPJ, mark that the purchase is for business purposes and tap Save.',
      },
      passoCartaoAtendente: {
        titulo: 'Add the assistant so they add the card',
        texto:
          'On "Your account is connected", tap Done. Then add the assistant to your business portfolio with finance permission: they are the one who adds the card.',
      },
      alertaCartao:
        'Without a valid card on the WhatsApp account, the number connects but cannot send any message.',
      depois:
        'Meta may send an email saying it will verify the business. That does not block the connection: the number already shows as connected in Zenda.',
    },
    custos: {
      janelaTitulo: 'The 24-hour window',
      janela:
        'Each patient message opens 24 hours in which the assistant replies freely. Example: they write at 10am Monday; until 10am Tuesday, she replies normally.',
      foraTitulo: 'After the window closes',
      fora: 'To reach them again, only an approved template works. Since 10/01/2026 Meta charges every delivered message, inside or outside the window.',
      colunaTipo: 'Delivered message',
      colunaValor: 'Price',
      servico: 'Reply inside the 24-hour window',
      servicoGratisAte: 'free until 09/30/2026',
      utilidade: 'Utility template: reminder, confirmation, appointment notice',
      marketing: 'Marketing template: promotion, campaign, news',
      nota: (cartaoDaAtendente: boolean) =>
        `Prices per delivered message in Brazil, from Meta's official rate card effective 10/01/2026. They are charged to the card ${cartaoDaAtendente ? 'of the assistant, added' : 'added'} on the number's WhatsApp account, in BRL.`,
      fonte: "Meta's official pricing",
    },
    onboarding: {
      checklist: {
        login:
          'Can they log into their Facebook/Meta account RIGHT NOW? (it is step 1, and the one that stalls most)',
        phone: 'Phone with WhatsApp Business open, to confirm from the device.',
        identity: 'Company tax ID and the name patients should see already decided.',
        cardYours: 'You have YOUR card at hand: the card is yours, not theirs.',
        adminAtEnd:
          'They know they will add you as an administrator at the end. Without that you cannot attach the card.',
        cardTheirs: 'Credit card at hand: Meta bills their account, not yours.',
      },
      invite: {
        opening: (primeiro: string) =>
          `Hi ${primeiro}! So I can look after your patients over WhatsApp, Meta (who owns WhatsApp) asks for a quick registration: it takes about 10 minutes and we do it together, over the phone.`,
        attendant: [
          'Before we book a time, check 2 things:',
          '',
          '1) Can you log into your Facebook account right now? It is the first step of the registration and the one that stalls most. If the password is gone, recover it beforehand.',
          '2) The number on the WhatsApp Business app, up to date, with the phone nearby. The regular WhatsApp app does not work.',
          '',
          'You do NOT have to register any card. At the end you will only add me as an administrator of your account: and I take care of the Meta billing, already included in what you pay me.',
          '',
          'About that access: the account stays yours, under your own company, and so does the number: if we ever stop working together, you remove me and keep everything. I need the access so I can attach my card and clear Meta issues without bothering you every time.',
        ],
        client: (url: string) => [
          'Before we book a time, check 3 things:',
          '',
          '1) Can you log into your Facebook account right now? It is the first step of the registration and the one that stalls most. If the password is gone, recover it beforehand.',
          '2) A valid credit or debit card at hand.',
          '3) The number on the WhatsApp Business app, up to date, with the phone nearby. The regular WhatsApp app does not work.',
          '',
          'About the card: the account is YOURS, under your own company, and you can see what you spent whenever you want. Meta bills the messages straight to you (it is usually little). I have no access to your card and I charge nothing per message: Meta requires the account to be in the name of whoever owns the number, and the number stays yours.',
          '',
          `Meta's official step-by-step for adding the card: ${url}`,
        ],
        closing: (atendente: string) =>
          `Tell me a time that works and we do it together.

${atendente}`,
      },
    },
  },

  verification: {
    title: 'Verification console',
    subtitle:
      'The two API calls Meta requires to approve the app: one per permission. This screen exists to be recorded, and goes away once approval comes through.',
    ready: 'Test number ready',
    notReady: 'Not configured',
    missingEnv:
      'The test number credentials are missing from .env. They are under Use cases → Connect with WhatsApp → Step 1.',
    testNumber: 'Test number',
    account: 'Account (WABA)',
    graphVersion: 'Graph API',
    sendTitle: 'Send a message',
    sendSub:
      'Record this block on its own. Meta needs to see the message leaving here and arriving on WhatsApp: film the screen and the phone.',
    to: 'To (with country code)',
    toHint:
      "The number must be on the test number's list of verified recipients: up to 5, registered in the Meta dashboard.",
    type: 'Type',
    typeTemplate: 'Template',
    typeText: 'Free-form text',
    typeHintTemplate: 'Always works. It is the only way to start a conversation.',
    typeHintText:
      'Only works while the 24-hour window is open: the recipient must have written first.',
    message: 'Message',
    templateApproved: 'Approved template',
    templateHint:
      'hello_world already exists in every test account: nothing to create for the first send.',
    sendButton: 'Send via WhatsApp',
    sending: 'Sending…',
    sendOk: 'Message accepted by Meta',
    sendTo: (numero: string, o: string) => `to ${numero} · ${o}`,
    templateTitle: 'Create a template',
    templateSub:
      'Record this block as a separate video. Showing both permissions in one video is a documented rejection reason.',
    templateName: 'Name',
    templateNameHint: 'Lowercase, digits and underscore. The name is corrected automatically.',
    templateBody: 'Body',
    templateBodyHint:
      'No specialty, procedure or diagnosis. Templates pass through Meta and are read by a human reviewer: clinical information here is a health-data leak, and the request is blocked before it leaves.',
    createButton: 'Create template',
    creating: 'Creating…',
    templateOk: 'Template created in the Meta account',
    sampleText:
      "Hi! This is the practice assistant. I got your message and I'm checking on it now.",
    sampleTemplateName: 'zenda_appointment_confirmation',
    sampleTemplateBody:
      'Hello! Confirming your appointment tomorrow at 2pm. Reply YES to confirm or NO to reschedule.',
  },

  enums: {
    waType: {
      image: 'Photo',
      audio: 'Audio',
      video: 'Video',
      document: 'Document',
      sticker: 'Sticker',
      location: 'Location',
      contacts: 'Contact',
    } as Readonly<Record<string, string | undefined>>,
    clientStatus: { active: 'Active', paused: 'Paused', churned: 'Ended' },
    conversationStatus: {
      open: 'Open',
      pending: 'Awaiting',
      answered_by_doctor: 'Answered by the doctor',
      resolved: 'Resolved',
    },
    appointmentSource: {
      zenda: 'Zenda',
      google_calendar: 'Google Calendar',
      clinic_system: 'From the platform',
      import: 'Imported',
    },
    appointmentStatus: {
      requested: 'Requested',
      scheduled: 'Scheduled',
      confirmed: 'Confirmed',
      checked_in: 'Checked in',
      completed: 'Completed',
      cancelled: 'Cancelled',
      no_show: 'No-show',
    },
    channelState: {
      draft: 'Draft',
      verifying: 'Verifying',
      active: 'Active',
      degraded: 'Degraded',
      inactive_warning: 'Inactivity',
      disconnected: 'Disconnected',
      suspended: 'Suspended',
      offboarded: 'Ended',
    },
    quality: {
      GREEN: 'High quality',
      YELLOW: 'Medium quality',
      RED: 'Low quality',
      UNKNOWN: 'No reading',
    },
    qualityShort: { GREEN: 'High', YELLOW: 'Medium', RED: 'Low', UNKNOWN: 'No reading' },
    billing: {
      ok: 'Billing up to date',
      unknown: 'Billing not checked',
      missing_payment_method: 'No payment method',
      blocked: 'Billing blocked',
    },
    billingShort: {
      ok: 'Up to date',
      unknown: 'Not checked',
      missing_payment_method: 'No payment',
      blocked: 'Blocked',
    },
    modality: { in_person: 'In person', telehealth: 'Telehealth' },
    delivery: {
      none: '',
      queued: 'Queued',
      in_doubt: 'Unconfirmed',
      accepted: 'Accepted',
      held: 'Held',
      sent: 'Sent',
      failed: 'Failed',
      delivered: 'Delivered',
      read: 'Read',
      played: 'Played',
    },
  },

  alerts: {
    disconnectedTitle: 'Connection dropped',
    disconnectedText:
      'The number is no longer linked to Zenda. Nothing comes in and nothing goes out until it is reconnected.',
    suspendedTitle: 'Number suspended by Meta',
    suspendedText: 'The account was suspended. No message goes out until Meta reviews it.',
    noCardTitle: "No card on the doctor's account",
    noCardShort: 'No card at Meta',
    noCardText:
      'Their Meta account has no payment method: the number connects and does NOT send. Use the ready-made message below to walk them through it.',
    billingBlockedTitle: 'Billing blocked at Meta',
    billingBlockedText:
      'Meta blocked billing on this account. No message goes out until it is resolved.',
    billingUnknownTitle: 'Billing not checked',
    billingUnknownShort: 'Billing unread',
    billingUnknownText:
      'Meta has not confirmed a valid card on this account yet. Tap "Check now" on the channel card to read it again.',
    inactiveTitle: 'Doctor not opening the app',
    inactiveTextNoDays:
      'Meta flagged inactivity on this number. Without use, the connection drops.',
    inactiveText: (dias: number) =>
      `No signal from the app for ${dias} ${dias === 1 ? 'day' : 'days'}. Without use, the connection drops on its own.`,
    degradedTitle: 'Channel degraded',
    degradedText: 'Delivery is unstable on this number. Worth watching today.',
    qualityRedTitle: 'Low quality',
    qualityRedText: 'Too many patient blocks. The sending limit can drop at any moment.',
    qualityYellowTitle: 'Quality slipping',
    qualityYellowText: 'Meta downgraded this number’s quality. Cut back on bulk sends.',
    noWhatsappTitle: 'No WhatsApp connected',
    noWhatsappShort: 'No WhatsApp',
    noWhatsappText: 'This client neither sends nor receives anything through Zenda yet.',
    overdueTitle: 'Payment overdue',
    overdueText:
      'The due date passed and the payment did not arrive. Chase it before the month turns.',
  },
  equipe: {
    emailObrigatorio: 'Enter the person’s e-mail.',
    horarioInvertido: 'The end must come after the start.',
    periodoIncompleto: 'Fill in the start and the end.',
    suasEquipes: 'Your teams',
    suasEquipesSub:
      'Work together with other assistants. Example: you and Bruna cover each other on vacation. Each doctor stays with whoever brought them.',
    nenhuma: 'No teams yet',
    nenhumaTexto:
      'Create one and invite who works with you. Each person decides which of their doctors join.',
    nomeDaEquipe: 'Team name',
    nomePlaceholder: 'E.g. Weekend cover',
    criar: 'Create team',
    resumo: (membros: number, medicos: number) =>
      `${membros} ${membros === 1 ? 'person' : 'people'} · ${medicos} ${medicos === 1 ? 'doctor' : 'doctors'}`,
    papel: { owner: 'Lead', admin: 'Admin', member: 'Member' },
    nivel: { atendimento: 'Front desk', gestao: 'Management' },
    nivelExplica: {
      atendimento: 'Conversations, schedule and patient record. Example: whoever covers a shift.',
      gestao:
        "Everything in Attendance, plus availability, locations and schedule settings. Example: whoever organizes the doctor's schedule.",
    },
    comoFunciona: 'How it works',
    comoFuncionaTexto:
      'Each person picks which doctors to share. Team members see, reply to and book for those doctors. Number, card and billing stay only with whoever brought the doctor.',
    naoEncontrada: 'Team not found',
    lideradaPor: (nome: string) => `Led by ${nome}`,
    renomear: 'Rename',
    arquivar: 'Archive',
    sair: 'Leave team',
    sairDe: (nome: string) => `Leave ${nome}?`,
    sairExplica:
      'You stop seeing the others’ doctors. If nobody else from your operation stays, your doctors leave the team too.',
    arquivarEquipe: (nome: string) => `Archive ${nome}?`,
    arquivarExplica: 'Everyone stops seeing each other’s doctors. The activity history is kept.',
    membros: 'People',
    membrosSub:
      'Leader: changes roles, hands over leadership, archives the team and sees billing. Admin: invites and removes members. Member: serves the shared doctors.',
    voce: 'you',
    voceMaiusculo: 'You',
    quemCriou: 'created the team',
    medicosCompartilhados: (n: number) =>
      n === 0 ? 'no shared doctors' : `${n} shared ${n === 1 ? 'doctor' : 'doctors'}`,
    papelDe: (nome: string) => `${nome}’s role`,
    tornarLider: 'Make lead',
    transferir: 'Transfer',
    transferirPara: (nome: string) => `Hand the lead over to ${nome}?`,
    transferirExplica: (nome: string) =>
      `${nome} becomes the lead and you become an admin. Billing of other operations’ doctors only shows to the new lead after each owner confirms again.`,
    remover: 'Remove',
    removerPessoa: (nome: string) => `Remove ${nome}`,
    removerExplica: (nome: string) =>
      `${nome} stops seeing the team’s doctors right away. If they were the last one from their operation, their doctors leave too.`,
    convidar: 'Invite',
    emailDeQuem: 'Email of who you want to invite',
    emailPlaceholder: 'Their email on Zenda',
    papelNoConvite: 'Role in the invite',
    conviteEnviado: (email: string) =>
      `If ${email} has a Zenda account, the invite shows up the next time they sign in.`,
    conviteDe: (quem: string, papel: 'admin' | 'member') =>
      `invited by ${quem} as ${papel === 'admin' ? 'admin' : 'member'}`,
    cancelarConvite: 'Cancel invite',
    faixaConvite: (n: number) =>
      n === 1 ? 'You have an invite to join a team' : `You have ${n} invites to join teams`,
    convitesParaVoce: 'Invites for you',
    convitesParaVoceSub:
      "To join, you accept the confidentiality terms: you will serve patients of other people's doctors.",
    convidadaPor: (quem: string, papel: 'admin' | 'member') =>
      `${quem} invited you as ${papel === 'admin' ? 'admin' : 'member'}`,
    verEAceitar: 'Review and accept',
    recusar: 'Decline',
    recusarConvite: (nome: string) => `Decline the invite to ${nome}?`,
    naoReceberMais: 'Don’t send me invites from this team again',
    entrarEm: (nome: string) => `Join ${nome}`,
    termoSub: 'Read the confidentiality terms. You will serve patients of other people’s doctors.',
    liETermo: 'I have read and accept the confidentiality terms',
    aceitarEEntrar: 'Accept and join',
    medicos: 'Doctors',
    medicosSub: 'What each operation shared with the team, and at which level.',
    daSuaOperacao: 'From your operation',
    deOutrasOperacoes: 'From other operations',
    nadaCompartilhado: 'You have not shared any doctor with this team yet.',
    soQuemAdministra: 'Doctors of your operation are shared by whoever manages it.',
    ninguemCompartilhou: 'Nobody has shared doctors with this team yet.',
    daPessoa: (nome: string) => `from ${nome}`,
    nivelDe: (nome: string) => `${nome}’s level`,
    retirar: 'Remove',
    retirarMedico: (nome: string) => `Remove ${nome} from the team?`,
    retirarExplica: (nome: string) =>
      `The team stops seeing ${nome}’s conversations, schedule and records right away. Conversations assigned to someone outside go back to you.`,
    declararDeNovo: (lider: string) => `Confirm again naming ${lider} so the lead can see billing`,
    declararPara: (nome: string) => `Authorization statement: ${nome}`,
    confirmar: 'Confirm',
    compartilharMedicos: 'Share doctors',
    compartilharCom: (nome: string) => `Share with ${nome}`,
    compartilharSub: 'Pick the doctors and the level. Number, card and record stay only with you.',
    compartilhar: (n: number) =>
      n === 0 ? 'Share' : `Share ${n} ${n === 1 ? 'doctor' : 'doctors'}`,
    quaisMedicos: 'Which doctors',
    todosOsMedicos: 'All',
    nenhumMedico: 'None',
    qualNivel: 'At which level',
    avisoFinanceiro: (lider: string) =>
      `${lider}, who leads the team, will see these doctors’ monthly fee, due date and payments. Other members don’t.`,
    confirmoDeclaracao: 'I confirm the statement above',
    sugestao: (medico: string, equipe: string) =>
      `Team ${equipe} asks for every new doctor to be shared. Share ${medico}?`,
    politicaTodos: 'Suggest sharing every new doctor',
    politicaTodosExplica:
      'When you add a doctor, the question comes pre-checked for this team. They only join with their own statement.',
    financeiro: 'Team doctors billing',
    financeiroSub:
      'Only the leadership sees it, split by operation: each contract belongs to whoever brought the doctor, so nothing is added up.',
    financeiroVazio: 'No operation has confirmed you as the lead yet.',
    turnos: 'Shifts',
    turnosSub:
      "Who covers what, and when. Shifts organize the work but never remove anyone's access.",
    dePlantaoAgora: 'On duty now',
    ninguemDePlantao: 'nobody',
    diasDaSemana: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    tirarTurno: 'Remove this shift',
    quem: 'Who',
    eu: 'Me',
    dia: 'Day',
    inicio: 'Start',
    fim: 'End',
    adicionarTurno: 'Shift',
    tipo: 'Type',
    folga: 'Day off',
    extra: 'Extra',
    marcar: 'Add',
    distribuir: 'Hand new conversations to whoever is on duty',
    distribuirExplica:
      'A new unassigned conversation goes to whoever is on shift with the fewest open conversations. Example: Ana with 3 and Bruna with 1, it goes to Bruna.',
    atividade: 'Activity',
    atividadeSub:
      'What was done through the team. You see your own and what happened to your doctors.',
    atividadeDoClienteSub:
      'Who did what for this doctor, and who from outside opened each conversation.',
    semAtividade: 'Nothing here yet',
    semAtividadeTexto: 'Actions show up here as soon as someone works.',
    pelaEquipe: 'via team',
    acao: {
      enviou_mensagem: 'sent a message',
      enviou_template: 'sent a template',
      enviou_midia: 'sent an attachment',
      escreveu_nota: 'wrote an internal note',
      abriu_conversa: 'opened a conversation',
      resolveu_conversa: 'resolved a conversation',
      reabriu_conversa: 'reopened a conversation',
      assumiu_conversa: 'took a conversation',
      soltou_conversa: 'released a conversation',
      etiquetou_conversa: 'tagged a conversation',
      tirou_etiqueta: 'removed a tag',
      marcou_consulta: 'booked an appointment',
      desmarcou_consulta: 'cancelled an appointment',
      remarcou_consulta: 'rescheduled an appointment',
      confirmou_consulta: 'confirmed an appointment',
      registrou_presenca: 'recorded attendance',
      editou_ficha: 'edited a patient record',
      cadastrou_paciente: 'added a patient',
      cadastrou_dependente: 'added a dependent',
      editou_dependente: 'edited a dependent',
      mudou_agenda: 'changed the schedule',
      criou_equipe: 'created the team',
      convidou: 'invited someone',
      entrou_na_equipe: 'joined the team',
      saiu_da_equipe: 'left the team',
      removeu_da_equipe: 'removed someone from the team',
      mudou_papel: 'changed someone’s role',
      transferiu_lideranca: 'handed over the lead',
      compartilhou_medico: 'shared a doctor',
      revogou_medico: 'removed a doctor',
      declarou_autorizacao: 'confirmed a doctor’s authorization',
    },
  },
  ui: {
    hoje: 'Today',
    limpar: 'Clear',
    semMensagensProntas: 'No canned messages yet. Create them in Settings, Canned messages.',
    faltaPreencher: 'missing data',
    avisos: 'Notices',
    fecharAviso: 'Close notice',
    calendario: 'Calendar',
    anterior: 'Previous',
    proximo: 'Next',
    limparData: 'Clear date',
    abrirCalendario: 'Open calendar',
    mensagensProntas: 'Canned messages',
    falhaAoBaixar: 'Could not download the file. Try again.',
    falhaNoAudio: 'Could not open the audio. Try again.',
    falhaNoArquivo: 'Could not send the file. Try again.',
    templateReabre: 'After 24 hours, only an approved template reopens the conversation',
  },

  grade: {
    livres: (min: number) => `${Math.floor(min / 60)} h ${min % 60} min free`,
    resumoDoDia: (n: number, livresMin: number) =>
      `${n} ${n === 1 ? 'appointment' : 'appointments'} · ${Math.floor(livresMin / 60)} h ${livresMin % 60} min free`,
    cliqueParaMarcar: (min: number) => `Click to book ${min} min, or drag to choose the duration.`,
    arrasteDica:
      'Drag to open hours; drag over the green to remove. The day name fills or clears the row.',
    abrirOuLimpar: (dia: string) => `Open or clear all of ${dia}`,
    igualSegunda: 'Same as Monday',
    igualSegundaDica: 'Repeats Monday from Tuesday to Friday.',
    vinteQuatroDica: 'Shows all 24 hours, for doctors who work at night or early morning.',
    diaInteiroDica: 'Shows all 24 hours of the day, including hours nobody works.',
    abertoPorExcecao: 'opened by exception',
  },

  integracao: {
    nativa: 'Native (Zenda)',
    conflitos: (n: number) => (n === 1 ? 'One conflict to resolve' : `${n} conflicts to resolve`),
    conflitoAjuda:
      'The patient booked a slot on the platform that was already taken here. The slot really is taken, so the platform is what needs fixing: call them and rebook.',
    semNome: 'Unnamed patient',
    resolvido: 'Resolved',
    resolvidoDica: 'Removes it from here. Use it after talking to the patient.',
    nomeSimulado: 'Doctoralia patient',
    simularDica: 'Test: pretends a patient booked on the other side.',
    desvincular: 'Unlink',
    desvincularDica: 'Deactivates. Appointments that came from there stay here.',
    reservouLa: 'The patient booked there:',
    data: 'Date',
    reservar: 'Book',
  },

  conectarMeta: {
    indisponivel: 'The connection with Meta is not available right now.',
    titulo: (medico: string) => `Connect ${medico}'s WhatsApp`,
    lede: 'manages your schedule and needs your permission to reply to your patients on WhatsApp on your behalf.',
    continuaSeu:
      'The number is still yours, and so is the Meta account. You can remove the access whenever you want.',
    precisaBusiness:
      'The number must be on the WhatsApp Business app, updated, with the phone nearby. Meta rejects numbers on regular WhatsApp.',
    cartaoDaOperacao: (operacao: string) =>
      `At the end, add ${operacao} to your portfolio with financial permission: they add the card.`,
    cartaoDoMedico:
      'At the end, tap "Add payment method" and add a valid card. Without it the number connects but sends no messages.',
    verPassos: 'See the steps',
    naoLiberada: (operacao: string) =>
      `WhatsApp connection is not enabled here yet. Let ${operacao} know before trying again.`,
    passo: (passo: string) => `In Meta's window, step: ${passo}`,
    aguardando: 'Waiting for Meta…',
    conectar: 'Connect my WhatsApp',
    rodape:
      'You will sign in with your Facebook account. Zenda never sees or stores your password.',
    guiaTitulo: 'Connection steps',
    guiaSub: "Keep this window open next to Meta's window and follow in order.",
    custos: 'Card, 24-hour window and what Meta charges',
  },

  disp: {
    extraSemHorario: 'To open extra hours, say from what time to what time.',
    soUmHorario: 'Fill in both the start and the end, or leave both empty.',
    fimAntes: 'The end must come after the start.',
    ateAntes: '"Until" must come after the start date.',
    desativarLocal: 'Deactivate. This location’s rules stop with it.',
    teleconsulta: 'Telehealth',
    adicionarLocal: 'Add location',
    ate: 'until',
    ateDica: 'Empty = no end date',
    validoAte: 'Valid until',
    nomeDoLocal: 'Location name',
    cidade: 'City',
    criar: 'Create',
    excecoesTitulo: 'Exceptions on specific dates',
    excecoesAjuda:
      'The grid above is the routine that repeats. Here you break the routine on one date only, without changing it: this is where you open or close hours on a specific day.',
    naoAtende: 'Closed',
    atendeExtra: 'Extra hours',
    extra: 'Extra',
    naoAtendeDica: 'Removes what the routine would create that day: holiday, vacation, conference.',
    atendeExtraDica:
      'Creates hours the routine does not have: a Saturday clinic, a longer afternoon, a day in the middle of a vacation.',
    dataDaExcecao: 'Exception date',
    ateQuando: 'Until',
    ateQuandoDica: 'Until when. Empty = only the day next to it',
    vazioDiaInteiro: 'Empty = whole day',
    obrigatorio: 'Required',
    motivo: 'Reason',
    semExcecoes: 'The routine applies to every day.',
    regrasTitulo: 'Booking rules',
    antecedenciaRotulo: 'Only accept with at least',
    antecedenciaDica:
      '…of notice. A slot starting sooner than that can no longer be booked. It keeps the doctor from finding out about an appointment with the patient already at the door.',
    horizonteRotulo: 'Open the schedule up to',
    horizonteDica:
      '…ahead. Without this cap, availability with no end date lets people book years ahead.',
    deslocamentoRotulo: 'Changing address takes',
    deslocamentoDica:
      'Gap required between two appointments at different locations on the same day.',
    dias: 'days',
    duracaoInvalida: 'The duration must be between 5 and 480 minutes.',
    tiposTitulo: 'Appointment types',
    tiposAjuda:
      'What shows up when booking, with each one’s duration. The duration can still be changed when booking: this is the type’s default.',
    tipoPlaceholder: 'Follow-up, first visit, squeeze-in…',
    semTipos: 'No types yet. Without a type, booking asks for the duration every time.',
    tipoComumDica:
      'It applies to every doctor in your operation, so it is not edited from one client’s page. If this doctor uses another duration, create a type of their own.',
    todos: 'all',
    salvar: 'Save',
    cancelar: 'Cancel',
    editarTipo: 'Change the name or the duration',
    desativar: 'Deactivate',
    desativarTipo: 'Removes it from booking. Appointments already booked with it do not change.',
    diaInteiro: 'whole day',
    remover: 'Remove',
    salvo: 'saved',
    nomeDoTipo: 'Type name',
    duracaoMin: 'Duration in minutes',
  },

  marcar: {
    escolhido: (quando: string) => `Chosen: ${quando}`,
    ver: 'show',
    periodos: { qualquer: 'Any', manha: 'Morning', tarde: 'Afternoon' },
    titulo: 'Book appointment',
    periodo: 'Time of day',
    fechar: 'Close',
    diasQuePode: 'Days the patient can come',
    dicaTipo: 'Picking a type fills in the duration next to it.',
    dicaTipoHorario:
      'Picking a type sets the duration. Changing the duration to a value no type has clears this field.',
    semTipo: 'No type',
    dicaDuracao: 'Decides which slots fit, so it changes the list.',
    dura: 'Lasts',
    min: 'min',
    diaAnterior: 'Previous day with openings',
    escolherDia: 'Pick a day on the calendar',
    proximoDia: 'Next day with openings',
    procurando: 'Searching…',
    nenhumHorario: 'No free slot with these filters.',
    afrouxe: 'Try loosening the days, or squeeze in.',
    assunto: 'Subject (optional), e.g. Botox, follow-up',
    assuntoDica: 'Internal note: the patient never sees it.',
    repetir: 'Repeat',
    vezesACada: (n: number) => `${n === 1 ? 'time' : 'times'}, every`,
    semanas: (n: number) => (n === 1 ? 'week' : 'weeks'),
    conferindo: 'Checking the dates…',
    conflitos: (n: number) =>
      n === 1
        ? '1 date is taken and will be skipped. Turn on squeeze-in to book anyway.'
        : `${n} dates are taken and will be skipped. Turn on squeeze-in to book anyway.`,
    conflitosNoIntervalo: (n: number) =>
      n === 1
        ? '1 date already has an appointment in this slot and will be skipped. Turn on squeeze-in to book anyway.'
        : `${n} dates already have an appointment in this slot and will be skipped. Turn on squeeze-in to book anyway.`,
    dicaEncaixe:
      'Also shows full slots. Squeezing in adds one more person to the block. It does not cancel anyone.',
    dicaEncaixeHorario:
      'Allows two appointments in the same slot. It does not cancel anyone, and it is not needed to book outside the routine hours.',
    encaixe: 'Squeeze in',
    marcando: 'Booking…',
    marcar: 'Book',
    marcarN: (n: number) => `Book ${n}`,
    foraDaJanela: 'Outside the hours this doctor works that day. You can still book.',
    estouraJanela: 'This appointment runs past the end of the working hours. You can still book.',
    buscarPaciente: 'Search patient by name or phone',
    nenhumPaciente: 'No patient of this client with that name.',
    trocar: 'change',
  },

  reabrir: {
    criarTemplate: 'Create a template',
    carregando: 'Loading templates…',
    semAprovadoPrimeira:
      'This doctor has no template approved by Meta yet, and the first message to the patient can only be a template.',
    semAprovadoJanela:
      'This doctor has no template approved by Meta yet. Without one you cannot send the first message or write after 24 hours.',
    escolha: 'Choose an approved template…',
    dicaComeca: 'Starts the conversation: the patient receives it and can reply here',
    dicaReabre:
      'Reopens the conversation: the patient receives it and the 24-hour window starts again',
    enviando: 'Sending…',
    enviar: 'Send template',
  },

  novaConversa: {
    comecar: 'Start a conversation',
    titulo: (nome: string) => `Message ${nome}`,
    subtitulo: 'The first message goes through an approved template.',
    botao: 'Message',
    qualMedicoHint:
      'Choose which doctor to write through: the message goes out from their WhatsApp, and the conversation stays with them.',
    abrirConversa: 'Open the conversation',
    precisaAutorizacao: 'The patient’s consent is not recorded yet',
    porQue:
      'Meta only lets a business write first to people who agreed to receive messages. Say how they agreed and move on to the template.',
    registrarEContinuar: 'Record and continue',
    contatos: 'Contacts without a conversation',
    contatoDe: (medicos: string) => `Contact · ${medicos}`,
  },

  piloto: {
    titulo: 'Autopilot',
    modos: { off: 'Off', copiloto: 'Copilot', piloto: 'Autopilot' },
    modosCurtos: { off: 'Off', copiloto: 'Copilot', piloto: 'Auto' },
    modosLongos: {
      off: 'Nothing changes: you answer everything.',
      copiloto: 'The AI drafts the reply in the box. Nothing goes out without you.',
      piloto: 'The AI answers what it knows. The rest stays with you.',
    },
    herdar: 'Same as the operation',
    precisam: (n: number) => (n === 1 ? '1 needs you' : `${n} need you`),
    nenhumPrecisa: 'Nothing needs you',
    pausas: {
      orcamento: 'paused: monthly budget',
      kill: 'paused by the platform',
      ia_indisponivel: 'paused: AI unavailable',
    },
    estados: {
      responding: 'Autopilot',
      needs_human: 'Needs you',
      taken_over: 'Taken over',
    },
    novas: (n: number) => `+${n} new`,
    respondeEm: (t: string) => `autopilot replies in ${t}`,
    respondoEu: "I'll answer",
    aguarda: (hora: string) => `autopilot answers at ${hora} if nobody replies`,
    aguardaDica:
      'Someone replied in this conversation a moment ago, and the autopilot does not talk over whoever is answering. The window is in Hours and rhythm.',
    lendo: 'autopilot reading the conversation',
    preparando: 'preparing the suggestion',
    tirar: 'Take from autopilot',
    tirarDica: 'The AI leaves this conversation until you hand it back.',
    devolver: 'Hand back to autopilot',
    selo: 'autopilot',
    corrigir: 'Correct',
    corrigirDica: "Opens the autopilot's text in the box. Your version becomes a memory proposal.",
    corrigindo: "Correcting the autopilot's answer: when sent, it becomes a memory proposal.",
    cancelar: 'Cancel',
    devolverBloqueado: 'Patient without autopilot: remove the mark on the card to hand back.',
    atrasada: (prazo: string) => `Past the deadline: ${prazo} of your working hours`,
    seloDica: 'Sent by the autopilot. Only you see this label.',
    sugestao: 'autopilot suggestion',
    descartar: 'Discard',
    semPilotoNaConversa: 'Patient without autopilot: the AI never joins their conversations.',
    pendente: (assunto: string) => `You said you'd get back on: ${assunto}.`,
    pendenteExplica:
      "The autopilot won't touch this topic in this conversation until you release it (or for 24 h).",
    pendenteLiberar: 'Done',
    aprender: {
      curto: 'Remember for next time?',
      editar: 'Edit',
      titulo: (medico: string) => `Add to ${medico}'s memory?`,
      texto: 'Next time someone asks this, the autopilot answers like this.',
      pergunta: 'Question',
      resposta: 'Answer',
      adicionar: 'Add',
      agoraNao: 'Not now',
      feito: 'Added to memory.',
    },
    hoje: {
      titulo: 'Autopilot today',
      respondidas: 'answered',
      precisam: 'needed you',
      assumidas: 'taken over',
      motivo: (m: string) => `Most common reason: ${m}`,
      semMotivo: 'No escalations today.',
      desligado: 'The autopilot is off. Turn it on in the sidebar.',
      orcamento: (usado: string, limite: string, pct: number) =>
        `Monthly budget: ${usado} of ${limite} (${pct}%).`,
      orcamentoEstourado: 'Monthly budget reached: the autopilot is copilot until next month.',
    },
    aba: {
      nav: {
        resumo: 'Summary',
        memoria: 'What it knows',
        estilo: 'How it writes',
        horarios: 'When it answers',
        testar: 'Test',
        aprendizados: 'Learnings',
        historico: 'History',
      },
      navTexto: {
        resumo: 'How the autopilot handles this doctor, and whether it is ready.',
        memoria: 'The autopilot answers only from what is here, and never makes things up.',
        estilo: 'So the patient feels it is the same you as always.',
        horarios: 'When it answers, how long it waits and the fixed phrases.',
        testar: 'Chat as a patient or run the test questions. Nothing goes to WhatsApp.',
        aprendizados: 'Proposals made from your answers. Nothing goes in without your click.',
        historico: 'Each time the memory is saved it becomes a version. You can go back.',
      },
      pronto: 'Ready to turn on?',
      checkIa: 'AI configured in this environment',
      checkMemoria: (assuntos: number, perguntas: number) =>
        `Memory with ${assuntos} ${assuntos === 1 ? 'fact' : 'facts'} and ${perguntas} ${perguntas === 1 ? 'question' : 'questions'}`,
      checkTestes: (ok: number, total: number) => `Tests: ${ok} of ${total} passed`,
      checkTestesNenhum: 'No test questions yet',
      checkAprendizados: (n: number) =>
        n === 0
          ? 'No learnings waiting'
          : `${n} ${n === 1 ? 'learning' : 'learnings'} waiting for you`,
      ir: 'Open',
      informacoes: 'Facts',
      informacoesTexto: 'Insurance, prices, address, parking, late policy.',
      colunaAssunto: 'Topic',
      colunaResposta: 'What to say',
      pacienteVe: 'The patient may know',
      pacienteNaoVe: 'Only the AI knows: never tells the patient',
      perguntasTexto: 'The exact way to answer the most common questions.',
      regrasTexto2: 'What the autopilot never does. E.g. "never share the surgery price".',
      naoSalvo: 'Unsaved changes',
      descartarAlteracoes: 'Discard',
      vazio: 'Nothing here yet.',
      ritmoTitulo: 'Pace',
      ritmoTexto: "How long after the patient's last message it answers, to feel human.",
      limitesTitulo: 'Limits',
      limitesTexto: 'When it stops and calls you.',
      posicao: "This doctor's setting",
      posicaoTexto: 'Overrides the general control in the sidebar.',
      vale: (m: string) => `In effect: ${m}`,
      iaPronta: (modelo: string) => `AI ready (${modelo}).`,
      iaFora: 'The AI is not configured in this environment: everything escalates to you.',
      memoria: 'Memory',
      memoriaTexto:
        'What the autopilot knows about this practice. It answers only from this and never makes things up.',
      memoriaTamanho: (usado: number, teto: number) => `${usado} of ${teto} tokens`,
      memoriaCheia:
        'The memory is over the limit: the autopilot uses only the entries closest to the question.',
      sobre: 'About the practice',
      sobreTexto:
        'Insurance, prices, parking, late policy. Only what is marked "can share" goes out.',
      rotulo: 'Topic',
      textoDoCampo: 'What to say',
      podeInformar: 'can share',
      novoCampo: 'Add topic',
      perguntas: 'Questions and answers',
      novaPergunta: 'Add question',
      importar: (n: number) => `Import quick replies (${n})`,
      aprendidoEm: (d: string) => `learned on ${d}`,
      regras: 'Rules',
      regrasTexto: 'E.g. "never share the surgery price, always call me".',
      novaRegra: 'Add rule',
      estilo: 'Style',
      estiloTexto: "How you write to this doctor's patients.",
      tratamento: 'Form of address',
      voce: 'informal you',
      senhor: 'sir / madam',
      emoji: 'Uses emoji',
      assinatura: 'Signature',
      formalidade: 'Tone',
      formalidades: { informal: 'informal', neutra: 'neutral', formal: 'formal' },
      frases: 'Phrases you always use (one per line)',
      observacoes: 'Notes',
      rascunhoDeEstilo: 'Suggest from my messages',
      exemplos: 'Examples (up to 10)',
      exemplosTexto: '"A patient asks, I answer like this."',
      paciente: 'Patient',
      eu: 'Me',
      novoExemplo: 'Add example',
      salvar: 'Save memory',
      salvo: (v: number) => `Version ${v} saved`,
      versoes: 'Versions',
      versao: (v: number, quando: string, quem: string | null) =>
        `Version ${v} · ${quando}${quem === null ? '' : ` · ${quem}`}`,
      restaurar: 'Restore this one',
      aprendizados: 'Learnings',
      aprendizadosTexto: 'Proposals made from your answers. Nothing goes in without your click.',
      semAprendizados: 'No proposals right now.',
      aprovar: 'Approve',
      horarios: 'Hours and pace',
      respondeQuando: 'When the AI answers',
      sempre: 'Always',
      soNestes: 'Only during these hours',
      limiar: 'Minimum confidence',
      antecedencia: 'Minimum notice to offer a slot (min)',
      seguidas: 'Replies in a row before calling you',
      humanoAtivo: 'Silence after you or the doctor reply (min)',
      esperaMin: 'Minimum wait (s)',
      esperaMax: 'Maximum wait (s)',
      salvarConfig: 'Save',
      frasesTitulo: 'Fixed phrases',
      emergencia: 'Emergency message',
      emergenciaTexto: 'The only health answer. Goes out once a day, in any conversation state.',
      reservaDentro: 'When a slot is chosen, during your working hours',
      reservaFora: 'When a slot is chosen, outside your working hours',
      simulador: 'Simulator',
      simuladorTexto:
        'Chat as if you were a patient. Nothing goes to WhatsApp. Try to trick the AI and see it stop.',
      escreva: 'Write as a patient…',
      enviar: 'Send',
      limpar: 'Start over',
      acoes: {
        nada: 'would stay silent',
        responder: 'would answer',
        reservar: 'would send the booking phrase and call you',
        escalar: 'would call you',
        emergencia: 'would send the emergency message and call you',
      },
      porque: (m: string) => `Reason: ${m}`,
      portao: (n: number) => `stopped at gate ${n}`,
      fontes: 'Sources',
      testes: 'Test set',
      testesTexto:
        "This practice's most common questions. Run them before turning the autopilot on.",
      pergunta: "Patient's question",
      esperado: 'The answer must contain',
      esperaAcao: 'Expected',
      deveResponder: 'answer',
      deveEscalar: 'call you',
      adicionarTeste: 'Add',
      rodar: 'Run tests',
      passou: 'passed',
      falhou: 'failed',
      mes: 'This month',
      respondidasMes: 'answered',
      escaladasMes: 'escalated',
      custoMes: 'model cost',
      motivosMes: 'What the memory lacked most',
      sombra: 'Copilot agreement (7 days)',
      sombraTexto: (a: number, c: number) =>
        c === 0 ? 'No suggestions used yet.' : `${a} of ${c} suggestions sent unchanged`,
    },
    config: {
      titulo: 'Autopilot',
      expediente: 'Your working hours',
      expedienteTexto: 'Counts the "Needs you" deadline and picks the booking phrase.',
      prazo: 'Minutes until it turns red',
      email: 'E-mail for alerts with the tab closed',
      emailTexto: 'Counts per doctor only, never patient names.',
      emailDepois: 'Alert after (min)',
      orcamento: 'Monthly budget (R$)',
      orcamentoTexto: 'At 100% the autopilot turns into copilot on its own.',
      som: 'Alert sound',
      resumo: 'Daily summary by e-mail',
      salvar: 'Save',
      salvo: 'Saved',
    },
    dias: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    ficha: {
      titulo: 'Autopilot',
      sem: 'No autopilot',
      semTexto: (desde: string, motivo: string | null) =>
        `Since ${desde}${motivo === null ? '' : ` (${motivo})`}. The AI never joins this patient's conversations.`,
      com: 'The autopilot can answer this patient.',
      marcar: 'Mark as no autopilot',
      tirar: 'Remove the mark',
    },
    notificacao: (medico: string) => `A conversation for ${medico} needs you`,
  },
  ajuda: {
    botao: 'How it works',
    entendi: 'Got it',
    telas: {
      painel: {
        titulo: 'How the Overview works',
        resumo: 'A summary of what needs you now, across every doctor.',
        secoes: [
          {
            titulo: 'The numbers on top',
            itens: [
              'Clients needing action: a doctor with a dropped connection, falling quality or another WhatsApp problem. Click to see which.',
              'Windows closing: conversations where the patient wrote almost 24 hours ago. Reply first, because afterwards only a template works.',
              'Conversations waiting: the patient wrote and nobody replied.',
              'Overdue payments: a client fee past due and not marked as received.',
              'Monthly revenue: the sum of fees from active clients.',
            ],
          },
          {
            titulo: 'The lists',
            itens: [
              'Conversations waiting for a reply: ordered by the window that closes first. The badge on the right says who took it. Click to open the conversation.',
              "Today's schedule: today's appointments for every doctor, with each one's status.",
            ],
          },
        ],
        dica: 'Start your day here: what is red is what turns into a complaint from the doctor if it waits.',
      },
      conversas: {
        titulo: 'How Conversations work',
        resumo:
          'Every patient of every doctor in one list. On the left you pick the conversation; in the middle, you reply.',
        secoes: [
          {
            titulo: 'The list',
            itens: [
              'Search: by name, phone (any format) or doctor. Patients with no conversation show below, under "Contacts without a conversation": click to send the first message with a template.',
              'Filters: All, Waiting (needs a reply), Resolved, Mine (the ones you took) and Unassigned (nobody took them).',
              'Urgent: patient waiting 1 hour or more, or the 24-hour window closing in less than 2 hours.',
              'Badge on the avatar: pink dot is "needs a reply", lock is "window closed", green tick is "resolved".',
              'Badge on the right: who took the conversation. Yellow is you; colored, someone else.',
            ],
          },
          {
            titulo: 'Replying',
            itens: [
              "Reply goes to the patient's WhatsApp. Internal note stays in the team and never reaches the patient.",
              'The paperclip attaches a photo, audio or document. The star inserts a canned message (you create them in Settings).',
              '24-hour window: after the patient writes, you reply freely for 24 hours. After that, only an approved template.',
              'Coexistence number: the doctor can also reply from WhatsApp Business on the phone, with no window and no cost.',
            ],
          },
          {
            titulo: 'Organizing',
            itens: [
              'Take: the conversation stays with you and others see it has an owner. Release puts it back in the queue.',
              'Resolve: removes the conversation from the queue when nothing is left to do. If the patient writes again, it comes back.',
              'Tag: a label to find it later, like "waiting for test results".',
              'Contact card (button on the top right): appointments with this doctor, the ones with other doctors kept apart, and who else the patient talks to.',
              'Book: pick the doctor under "Book with". By default it is this conversation\'s doctor.',
            ],
          },
        ],
        dica: 'A routine example: filter "Urgent", take, reply and resolve. Repeat until it is empty.',
      },
      clientes: {
        titulo: 'How the Clients list works',
        resumo: 'Each client is a doctor you serve.',
        secoes: [
          {
            titulo: 'The columns',
            itens: [
              "Connection: whether the doctor's WhatsApp is up or has a problem.",
              'Conversations open now: how many are waiting for an action.',
              'Patients this month: how many talked to the doctor this month.',
              'Billing: whether your monthly fee with them is paid, open or overdue.',
            ],
          },
          {
            titulo: 'What to do here',
            itens: [
              "New client: registers the doctor. Then you connect their WhatsApp on the client's page.",
              'Click a client to see connection, patients, templates, billing and activity.',
            ],
          },
        ],
        dica: "A red dot in the Connection column comes first: while the WhatsApp is down, none of that doctor's messages arrive.",
      },
      cliente_geral: {
        titulo: 'Client overview',
        resumo: "The health of this doctor's WhatsApp and the workload they generate.",
        secoes: [
          {
            titulo: 'Connection',
            itens: [
              'Connect WhatsApp: creates the link the doctor opens to connect their number. No card needed.',
              "Channel type: Coexistence is a number that stays on WhatsApp Business on the doctor's phone.",
              'Quality: the score Meta gives the number. Low means many patients blocking or reporting.',
              'Sending tier: how many different people the number can message per day.',
              'App signal: with Coexistence, how many days since the doctor opened WhatsApp Business. Unused for weeks, Meta drops the connection.',
              'Check now: reads quality and limit from Meta again.',
              'Disconnect number: removes the number from Zenda. The conversations stay.',
            ],
          },
          {
            titulo: 'Volume',
            itens: [
              'Patients, conversations and appointments this month, compared with last month. It shows how much work the client takes.',
            ],
          },
        ],
        dica: "If a red alert shows up here, fix it first: it affects every one of the doctor's conversations.",
      },
      cliente_pacientes: {
        titulo: "The client's patients",
        resumo: 'Who already talked to this doctor on WhatsApp, and who you registered by hand.',
        secoes: [
          {
            titulo: 'The list',
            itens: [
              'Search: by name or number.',
              "Message: opens the conversation in Conversations, even with someone who never wrote. There you record the patient's consent, if missing, and send the first message with a template.",
              'Upcoming: only people with an appointment booked from now on. Good for confirming the week.',
              'New patient: registers by WhatsApp number. When the person writes, the message lands in this record.',
            ],
          },
          {
            titulo: 'The record',
            itens: [
              'Identification, contact, address and insurance. Changes save on their own.',
              'WhatsApp messages: record that the patient agreed to receive messages. Without it, the first message only goes to people who already wrote.',
              'Dependents: someone served without owning the WhatsApp, like a child. Messages go to the guardian.',
              'Notes: only the team sees them. Example: "prefers mornings".',
            ],
          },
        ],
        dica: 'The number identifies the patient: registering the right number avoids duplicate records.',
      },
      cliente_templates: {
        titulo: "The client's templates",
        resumo:
          'Ready messages approved by Meta. They are the only way to write to someone silent for more than 24 hours.',
        secoes: [
          {
            titulo: 'When to use them',
            itens: [
              'Appointment reminder, confirmation, follow-up: anything you send without the patient writing first.',
              "Each template belongs to this doctor's account. One doctor's template does not work for another.",
            ],
          },
          {
            titulo: 'The statuses',
            itens: [
              'In review: Meta is checking, usually within minutes.',
              'Approved: it shows up in the conversation to send.',
              'Rejected: fix the text and create a new one. A rejected one does not come back.',
              'Paused: many patients blocked it. Meta stops the template for a while.',
            ],
          },
          {
            titulo: 'Category and cost',
            itens: [
              'Utility: notice, reminder, confirmation. About R$ 0.035 per message.',
              'Marketing: promotion, news. About R$ 0.32, nine times more. If the text reads as advertising, Meta switches it to Marketing on its own.',
            ],
          },
        ],
        dica: 'An example Meta approves: "Hi {{1}}, your appointment is tomorrow at {{2}}. Confirm?" No specialty or diagnosis.',
      },
      cliente_cobranca: {
        titulo: "The client's billing",
        resumo: 'Tracking the monthly fee you charge this doctor.',
        secoes: [
          {
            titulo: 'How it works',
            itens: [
              'Each month shows its due date and status: open, received or overdue.',
              'Mark as received: when the doctor pays. Undo fixes a mistake.',
              'Zenda does not issue invoices or charge anything: it is just a reminder so you do not miss the date.',
            ],
          },
        ],
        dica: 'An overdue fee shows on the Overview under "Overdue payments".',
      },
      cliente_piloto: {
        titulo: 'Autopilot',
        resumo: 'What the AI knows about this doctor, when it answers and how to test it first.',
        secoes: [
          {
            titulo: 'How it works',
            itens: [
              'The AI answers as you, only from the memory. What it does not know goes to "Needs you".',
              'Health questions, complaints, robot tests or requests for a person: it stops and the conversation is "Taken over".',
              'Every number, time, price and insurer in the answer is checked against the memory before it goes out.',
            ],
          },
          {
            titulo: 'Before turning it on',
            itens: [
              'Write the memory and use the simulator, including trying to trick the AI.',
              'Build the test set and run it. Start in copilot.',
            ],
          },
        ],
        dica: 'Nothing from the simulator goes to WhatsApp.',
      },
      cliente_atividade: {
        titulo: "The client's activity",
        resumo: 'Who did what for this doctor: messages, appointments, records.',
        secoes: [
          {
            titulo: 'What it is for',
            itens: [
              'See what the team did, when and who. Example: who cancelled an appointment.',
              'With a team, it also shows who from outside opened each conversation.',
            ],
          },
        ],
        dica: 'Message content does not show here: only the action and who did it.',
      },
      agenda: {
        titulo: 'How the Schedule works',
        resumo: "One doctor's schedule at a time: days, hours and appointments.",
        secoes: [
          {
            titulo: 'The tabs',
            itens: [
              'Calendar: pick Day, Week or Month on the right. The arrows move one day, week or month, depending on the view, and clicking the period opens the calendar to jump to any date. Week shows the time grid, like Google Calendar: green is when the doctor works, and clicking an empty spot books there. Month shows the whole month; click a day to open it.',
              'When booking from a slot, the search also finds patients of other doctors. Picking one links them to this doctor, with no duplicate record.',
              "Availability: the doctor's routine (days and hours they work) and exceptions, like vacation or an extra Saturday.",
              "Integration: if the doctor's schedule is Doctoralia, what syncs and how.",
            ],
          },
          {
            titulo: 'On an appointment',
            itens: [
              'Reschedule, Patient confirmed, Showed up, No-show and Cancel.',
              'Overbooking: an appointment booked on top of another, on purpose.',
              'Telehealth: an online appointment.',
            ],
          },
          {
            titulo: 'Schedule on Doctoralia',
            itens: [
              'When the client uses Doctoralia, the truth is there: booking here reserves there first, and what the patient books there shows up here on its own.',
            ],
          },
        ],
        dica: 'Set the availability before booking: it is what shows the free slots.',
      },
      equipe: {
        titulo: 'How the Team works',
        resumo:
          'To work together with other assistants. Each one decides which doctors to share, and each doctor stays with whoever brought them.',
        secoes: [
          {
            titulo: 'People roles',
            itens: [
              "Leader: whoever created the team. Changes roles, hands over leadership, archives the team and sees the doctors' billing.",
              'Admin: invites and removes members. Example: the colleague who helps organize the team.',
              'Member: serves the shared doctors. Example: whoever covers a shift.',
            ],
          },
          {
            titulo: 'Levels for each shared doctor',
            itens: [
              'Attendance: conversations, schedule and patient record. For people who only serve.',
              "Management: everything in Attendance, plus availability, locations and schedule settings. For whoever organizes the doctor's schedule.",
              'Number, card and billing are never shared: they stay with whoever brought the doctor.',
            ],
          },
          {
            titulo: 'Shifts',
            itens: [
              'They say who covers what, and when. Example: Ana Monday to Wednesday, Bruna Thursday to Saturday.',
              'Day off removes someone from a day; Extra adds someone on a day outside the routine.',
              'Distribute new conversations: an unassigned conversation goes to whoever is on shift with the fewest open conversations.',
              'A shift does not remove access: off shift, the person still sees everything, they just stop receiving new conversations.',
            ],
          },
          {
            titulo: 'Invites and billing',
            itens: [
              "Invite: by the person's Zenda e-mail. They accept the confidentiality terms to join.",
              'Billing: only the leadership sees it, split by operation, because each contract belongs to whoever brought the doctor.',
            ],
          },
        ],
        dica: 'Example: you go on vacation. Share your doctors with Bruna at the Attendance level, and she covers without touching your schedule.',
      },
      configuracoes: {
        titulo: 'How Settings work',
        resumo: 'Your own preferences and texts you reuse.',
        secoes: [
          {
            titulo: 'Your preferences',
            itens: [
              'Language: changes the screens only for you, not the messages to patients.',
              'Your name in conversations: how the team sees you, and what replaces {atendente}.',
              'New message alerts: sound and notification in this browser. "Off my shift" limits alerts to doctors from your operation.',
            ],
          },
          {
            titulo: 'Texts to reuse',
            itens: [
              'Tags: to organize conversations. They apply to every client.',
              'Canned messages: replies you send all the time. They only go out with the 24-hour window open. Variables like {paciente} fill in on their own.',
              "Template drafts: a base text to create the same template for several doctors. Approval is requested in each doctor's Templates tab.",
            ],
          },
        ],
        dica: 'The difference that confuses people: a canned message is for within 24 hours and skips Meta; a template is approved by Meta and works after 24 hours.',
      },
    },
  },
};
